// Automotive Templates
// T8: "Garage Pro" — white/charcoal/red, Oswald + DM Sans, industrial performance shop
// T9: "Local Mechanic" — cream/navy/gold, Barlow Condensed + Nunito, warm neighborhood shop (LIGHT THEME)

export const AUTOMOTIVE_DEFAULT_COPY = {
  T8: {
    eyebrow: "ASE Certified — Northeast Ohio Auto Repair",
    headline: "We Fix It Right.",
    tagline: "Full-service auto repair for every make and model. Honest pricing, same-day service, backed by a 2-year warranty.",
    about: "We're not a dealership. We're better. Our ASE-certified technicians work on domestic and foreign vehicles alike, from routine maintenance to complete engine rebuilds. We use OEM and premium aftermarket parts, stand behind every job, and never sell you something you don't need.",
  },
  T9: {
    eyebrow: "Your Neighborhood Auto Shop Since 1994",
    headline: "Honest Work. Fair Prices. Your Car Back Today.",
    tagline: "We treat your car like it's ours — and we treat you like a neighbor, because you are. No upsells. No runaround. Just good work at a fair price.",
    about: "Some shops want to upsell you on every visit. We want to keep your car running and keep you coming back because you trust us — not because we gave you no choice. We've been serving this community for over 30 years, building our reputation one honest repair at a time.",
  },
};

export const AUTOMOTIVE_DEFAULT_SERVICES = {
  T8: [
    { icon: "fa-gear", title: "Engine &amp; Drivetrain", desc: "Diagnostics, rebuilds, timing belts, head gaskets, and everything in between. We tackle the jobs other shops turn away." },
    { icon: "fa-circle-stop", title: "Brakes &amp; Suspension", desc: "Pads, rotors, calipers, shocks, struts, and full alignment service. If it handles or stops, we service it." },
    { icon: "fa-temperature-half", title: "AC &amp; Heat", desc: "Refrigerant recharge, leak detection, compressor replacement — stay comfortable through Ohio winters and summers." },
    { icon: "fa-car-battery", title: "Electrical &amp; Diagnostics", desc: "Check engine lights, ABS, TPMS, battery, alternator. We trace electrical faults down to the source — no guessing." },
    { icon: "fa-oil-can", title: "Oil Changes &amp; Maintenance", desc: "Conventional, full-synthetic, and high-mileage options. Fast in-and-out service with OEM-grade filters, every time." },
    { icon: "fa-arrows-spin", title: "Tires &amp; Alignment", desc: "New tires installed, rotated, and balanced. Computer wheel alignment to protect your tires and improve fuel economy." },
  ],
  T9: [
    { icon: "fa-oil-can", title: "Oil Changes &amp; Maintenance", desc: "In and out in under 30 minutes. Conventional, synthetic, or high-mileage — with a full multi-point inspection included at no extra charge." },
    { icon: "fa-circle-stop", title: "Brakes &amp; Rotors", desc: "Complete brake system service — pads, rotors, calipers, brake lines. We'll tell you exactly what needs replacement and show you why." },
    { icon: "fa-magnifying-glass-chart", title: "Check Engine Light", desc: "We pull the codes, trace them to the root cause, and give you a straight answer — not a parts-cannon approach. Diagnostics always free." },
    { icon: "fa-temperature-half", title: "AC &amp; Heating", desc: "Refrigerant recharge, compressor replacement, heater core, blend doors. We'll keep you comfortable through Ohio's full range of seasons." },
    { icon: "fa-arrows-spin", title: "Tires &amp; Alignment", desc: "New tires from top brands, installed and balanced. Computer alignment to protect your investment and improve handling and fuel economy." },
    { icon: "fa-gear", title: "Engine &amp; Transmission", desc: "From timing belts and head gaskets to transmission service — we handle the big stuff that other shops refer out. All makes, all models." },
  ],
};

export function buildAutoServiceCardsT8(services) {
  return services.map((s, i) => `
    <a href="#contact" class="srv-card anim d${(i % 3) + 1}">
      <div class="srv-icon"><i class="fa-solid ${s.icon}" aria-hidden="true"></i></div>
      <h3>${s.title}</h3>
      <p>${s.desc}</p>
      <span class="srv-card-link">Book Service <i class="fa-solid fa-arrow-right" aria-hidden="true"></i></span>
    </a>
  `).join('');
}

export function buildAutoServiceCardsT9(services) {
  return services.map(s => `
    <div class="srv-card anim d1">
      <div class="srv-icon"><i class="fa-solid ${s.icon}"></i></div>
      <h3>${s.title}</h3>
      <p>${s.desc}</p>
      <a href="#contact" class="srv-more">Schedule <i class="fa-solid fa-arrow-right"></i></a>
    </div>
  `).join('');
}

// T8: "Garage Pro" — charcoal/red, Oswald + DM Sans, full-bleed hero with floating booking card
const T8 = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>PLACEHOLDER_COMPANY | Full-Service Auto Repair</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link rel="preconnect" href="https://cdnjs.cloudflare.com">
<link href="https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;600;700&family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&display=swap" rel="stylesheet">
<link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css">
<style>
PLACEHOLDER_BRAND_COLORS_T8
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
html{scroll-behavior:smooth}
body{font-family:'DM Sans',sans-serif;background:var(--bg);color:var(--text);overflow-x:hidden}
img{display:block;max-width:100%}
#services,#about,#contact,#hours{scroll-margin-top:70px}
h1,h2,h3,.section-h2{text-wrap:balance}
a:focus-visible,button:focus-visible,input:focus-visible,select:focus-visible,textarea:focus-visible{outline:3px solid var(--red);outline-offset:3px}
.sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}
.skip-link{position:absolute;top:-100%;left:12px;z-index:9999;background:var(--red);color:#fff;font-family:'Oswald',sans-serif;font-size:14px;font-weight:600;letter-spacing:0.1em;text-transform:uppercase;padding:10px 20px;text-decoration:none;transition:top 0.2s}
.skip-link:focus{top:12px}

/* ── SCROLL ANIMATIONS ── */
.anim{opacity:0;transform:translateY(40px);transition:opacity 0.75s cubic-bezier(0.22,1,0.36,1),transform 0.75s cubic-bezier(0.22,1,0.36,1)}
.anim.left{transform:translateX(-48px)}
.anim.right{transform:translateX(48px)}
.anim.on{opacity:1!important;transform:none!important}
.d1{transition-delay:.08s}.d2{transition-delay:.18s}.d3{transition-delay:.28s}
.d4{transition-delay:.38s}.d5{transition-delay:.48s}.d6{transition-delay:.58s}
@media (prefers-reduced-motion: reduce) {
  .anim{opacity:1;transform:none;transition:none}
  .anim.left,.anim.right{transform:none}
  .pb-cell img{transition:none}
  .aph-main img,.aph-sm img{transition:none}
}

/* ── NAV ── */
nav{position:fixed;top:0;left:0;right:0;z-index:300;height:70px;background:var(--charcoal);display:flex;align-items:center;justify-content:space-between;padding:0 48px;border-bottom:2px solid var(--red)}
.nav-logo{font-family:'Oswald',sans-serif;font-size:22px;font-weight:700;color:#fff;text-transform:uppercase;letter-spacing:0.06em;text-decoration:none;display:flex;align-items:center;gap:12px}
.nav-logo-img{height:32px;width:auto;object-fit:contain;filter:brightness(0) invert(1)}
.nav-logo em{color:var(--red);font-style:normal}
.nav-links{display:flex;gap:32px;list-style:none}
.nav-links a{font-family:'DM Sans',sans-serif;font-size:12px;font-weight:700;letter-spacing:0.14em;text-transform:uppercase;color:rgba(255,255,255,0.55);text-decoration:none;transition:color 0.2s}
.nav-links a:hover{color:#fff}
.nav-right{display:flex;align-items:center;gap:20px}
.nav-phone{font-family:'Oswald',sans-serif;font-size:19px;font-weight:500;color:#fff;text-decoration:none;letter-spacing:0.04em;display:flex;align-items:center;gap:9px}
.nav-phone i{color:var(--red);font-size:14px}
.nav-cta{background:var(--red);color:#fff;font-family:'Oswald',sans-serif;font-size:13px;font-weight:600;letter-spacing:0.12em;text-transform:uppercase;padding:10px 22px;border:2px solid rgba(255,255,255,0.2);cursor:pointer;text-decoration:none;transition:background 0.2s,border-color 0.2s}
.nav-cta:hover{background:var(--red-dark);border-color:rgba(255,255,255,0.35)}

/* ── HERO ── */
.hero{margin-top:70px;position:relative;height:580px;overflow:visible;display:flex;flex-direction:column;justify-content:flex-start}
.hero-bg{position:absolute;inset:0;background:url('PLACEHOLDER_IMG/dextar-vision-gW34cv-Ojjs-unsplash.jpg') center/cover no-repeat;z-index:0}
.hero-overlay{position:absolute;inset:0;background:linear-gradient(to bottom,rgba(15,15,17,0.75) 0%,rgba(15,15,17,0.65) 60%,rgba(15,15,17,0.82) 100%);z-index:1}
.hero-overlay::after{content:'';position:absolute;inset:0;pointer-events:none;background-image:repeating-linear-gradient(-52deg,rgba(255,255,255,0.015) 0,rgba(255,255,255,0.015) 1px,transparent 1px,transparent 14px)}
.hero-content{position:relative;z-index:2;max-width:1200px;margin:0 auto;width:100%;padding:72px 48px 0;text-align:center}
.hero-eyebrow{display:inline-flex;align-items:center;gap:12px;font-family:'DM Sans',sans-serif;font-size:11px;font-weight:700;letter-spacing:0.24em;text-transform:uppercase;color:var(--red);margin-bottom:22px}
.hero-eyebrow::before,.hero-eyebrow::after{content:'';width:24px;height:2px;background:var(--red);flex-shrink:0}
.hero-h1{font-family:'Oswald',sans-serif;font-size:clamp(52px,6.5vw,88px);font-weight:700;text-transform:uppercase;line-height:0.95;letter-spacing:0.01em;color:#fff;margin-bottom:20px}
.hero-h1 em{color:var(--red);font-style:normal}
.hero-sub{font-size:17px;line-height:1.68;color:rgba(255,255,255,0.6);font-weight:300;max-width:580px;margin:0 auto}
.hero-trust-row{display:flex;align-items:center;justify-content:center;gap:28px;margin-top:28px}
.hero-trust-item{display:flex;align-items:center;gap:8px;font-size:12px;font-weight:600;letter-spacing:0.1em;text-transform:uppercase;color:rgba(255,255,255,0.55)}
.hero-trust-item i{color:var(--red);font-size:13px}

/* ── BOOKING CARD ── */
.booking-card-wrap{position:relative;z-index:10;max-width:1100px;margin:0 auto;padding:0 48px}
.booking-card{background:#fff;border-top:4px solid var(--red);box-shadow:0 20px 60px rgba(0,0,0,0.22),0 4px 16px rgba(0,0,0,0.12);padding:32px 36px}
.booking-card-label{font-family:'Oswald',sans-serif;font-size:13px;font-weight:600;letter-spacing:0.14em;text-transform:uppercase;color:var(--charcoal);margin-bottom:20px;display:flex;align-items:center;gap:10px}
.booking-card-label i{color:var(--red)}
.booking-fields{display:grid;grid-template-columns:1fr 1fr 1fr 1fr auto;gap:10px;align-items:end}
.bf-group{display:flex;flex-direction:column;gap:5px}
.bf-group label{font-size:10px;font-weight:700;letter-spacing:0.14em;text-transform:uppercase;color:var(--muted)}
.bf-input{width:100%;background:var(--bg);border:1px solid var(--border);padding:13px 15px;font-family:'DM Sans',sans-serif;font-size:14px;color:var(--text);outline:none;border-radius:0;transition:border-color 0.22s,background 0.2s}
.bf-input::placeholder{color:#b8b4ae}
.bf-input:focus{border-color:var(--red);background:#fff}
select.bf-input{cursor:pointer}
.bf-submit{background:var(--red);color:#fff;font-family:'Oswald',sans-serif;font-size:14px;font-weight:700;letter-spacing:0.12em;text-transform:uppercase;padding:13px 28px;border:none;cursor:pointer;white-space:nowrap;touch-action:manipulation;height:48px;display:flex;align-items:center;gap:9px;transition:background 0.2s,transform 0.15s}
.bf-submit:hover{background:var(--red-dark);transform:translateY(-1px)}
.bf-submit:active{transform:translateY(1px)}

/* ── TRUST BAR ── */
.trust-bar{background:var(--warm);border-bottom:1px solid var(--border)}
.trust-bar-inner{max-width:1200px;margin:0 auto;display:flex;border-top:1px solid var(--border)}
.trust-item{flex:1;padding:24px 28px;display:flex;align-items:center;gap:16px;border-right:1px solid var(--border)}
.trust-item:last-child{border-right:none}
.trust-icon{width:44px;height:44px;flex-shrink:0;background:var(--red);display:flex;align-items:center;justify-content:center;color:#fff;font-size:16px}
.trust-item h4{font-family:'Oswald',sans-serif;font-size:13px;font-weight:600;text-transform:uppercase;letter-spacing:0.06em;color:var(--charcoal);margin-bottom:2px}
.trust-item p{font-size:12px;color:var(--muted);font-weight:400;line-height:1.45}

/* ── SERVICES ── */
.services{background:var(--bg);padding:100px 48px}
.section-head{text-align:center;max-width:620px;margin:0 auto 64px}
.section-label{display:inline-flex;align-items:center;gap:10px;font-size:11px;font-weight:700;letter-spacing:0.22em;text-transform:uppercase;color:var(--red);margin-bottom:16px}
.section-label::before,.section-label::after{content:'';width:24px;height:2px;background:var(--red)}
.section-h2{font-family:'Oswald',sans-serif;font-size:clamp(32px,4vw,50px);font-weight:700;text-transform:uppercase;color:var(--charcoal);line-height:1.02;letter-spacing:0.01em}
.section-h2 em{color:var(--red);font-style:normal}
.section-sub{font-size:16px;color:var(--muted);font-weight:300;line-height:1.7;margin-top:14px}
.services-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:1px;background:var(--border);border:1px solid var(--border);max-width:1200px;margin:0 auto}
.srv-card{display:block;text-decoration:none;color:inherit;background:var(--bg);padding:40px 32px;position:relative;overflow:hidden;transition:background 0.3s,box-shadow 0.3s}
.srv-card::after{content:'';position:absolute;bottom:0;left:0;width:0;height:3px;background:var(--red);transition:width 0.45s cubic-bezier(0.22,1,0.36,1)}
.srv-card:hover{background:var(--warm);box-shadow:inset 0 0 0 1px var(--border)}
.srv-card:hover::after{width:100%}
.srv-icon{width:56px;height:56px;background:var(--warm);border:1px solid var(--border);display:flex;align-items:center;justify-content:center;margin-bottom:20px;transition:background 0.3s,border-color 0.3s}
.srv-icon i{font-size:22px;color:var(--red);transition:color 0.3s,transform 0.3s}
.srv-card:hover .srv-icon{background:var(--red);border-color:var(--red)}
.srv-card:hover .srv-icon i{color:#fff;transform:scale(1.1)}
.srv-card h3{font-family:'Oswald',sans-serif;font-size:19px;font-weight:600;text-transform:uppercase;letter-spacing:0.04em;color:var(--charcoal);margin-bottom:12px}
.srv-card p{font-size:14px;color:var(--muted);line-height:1.72;font-weight:400}
.srv-card-link{display:inline-flex;align-items:center;gap:8px;margin-top:16px;font-size:13px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:var(--red);transition:gap 0.2s}
.srv-card:hover .srv-card-link{gap:12px}

/* ── PHOTO BANNER ── */
.photo-banner{display:grid;grid-template-columns:2fr 1fr 1fr;height:440px;overflow:hidden;gap:3px;background:var(--charcoal)}
.pb-cell{overflow:hidden;position:relative}
.pb-cell img{width:100%;height:100%;object-fit:cover;transition:transform 7s ease,filter 0.4s;filter:brightness(0.88) saturate(1.05)}
.pb-overlay{position:absolute;inset:0;background:linear-gradient(to top,rgba(0,0,0,0.5) 0%,transparent 55%)}

/* ── STATS STRIP ── */
.stats-strip{background:var(--red);padding:52px 48px;display:flex;justify-content:center;position:relative;overflow:hidden}
.stats-strip::before{content:'';position:absolute;inset:0;pointer-events:none;background-image:repeating-linear-gradient(-52deg,rgba(0,0,0,0.07) 0,rgba(0,0,0,0.07) 1px,transparent 1px,transparent 18px)}
.stats-inner{position:relative;z-index:1;max-width:1000px;width:100%;display:flex;justify-content:space-between}
.stat-box{flex:1;text-align:center;padding:0 32px;border-right:1px solid rgba(255,255,255,0.2)}
.stat-box:last-child{border-right:none}
.stat-num{font-family:'Oswald',sans-serif;font-size:58px;font-weight:700;color:#fff;line-height:1;display:block;letter-spacing:-0.02em}
.stat-lbl{font-size:12px;font-weight:700;letter-spacing:0.14em;text-transform:uppercase;color:rgba(255,255,255,0.75);margin-top:8px;display:block}

/* ── ABOUT ── */
.about{background:var(--warm);padding:100px 48px 148px;position:relative}
.about::after{content:'';position:absolute;bottom:0;left:0;right:0;height:72px;background:var(--bg);clip-path:polygon(0 100%, 100% 0, 100% 100%);pointer-events:none}
.about-inner{max-width:1200px;margin:0 auto;display:grid;grid-template-columns:1fr 1fr;gap:80px;align-items:center}
.about-photos{display:grid;grid-template-columns:1.15fr 0.85fr;grid-template-rows:1fr 180px;gap:10px;height:540px}
.aph-main{grid-row:1/3;overflow:hidden;position:relative}
.aph-main img,.aph-sm img{width:100%;height:100%;object-fit:cover;transition:transform 7s ease}
.aph-sm{overflow:hidden}
.aph-stat{background:var(--red);display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:20px}
.aph-stat strong{font-family:'Oswald',sans-serif;font-size:44px;font-weight:700;color:#fff;line-height:1;display:block}
.aph-stat span{font-size:11px;font-weight:700;letter-spacing:0.12em;text-transform:uppercase;color:rgba(255,255,255,0.8);margin-top:6px;display:block}
.about-content h2{font-family:'Oswald',sans-serif;font-size:clamp(30px,3.8vw,50px);font-weight:700;text-transform:uppercase;color:var(--charcoal);line-height:1.02;margin-bottom:20px}
.about-content h2 em{color:var(--red);font-style:normal}
.about-content>p{font-size:16px;color:var(--muted);line-height:1.8;font-weight:300;margin-bottom:28px}
.about-checklist{list-style:none;display:flex;flex-direction:column;gap:12px;margin-bottom:32px}
.about-checklist li{font-size:15px;color:var(--text);font-weight:500;display:flex;align-items:center;gap:12px}
.about-checklist i{color:var(--red);font-size:13px;flex-shrink:0}
.cert-row{display:flex;flex-wrap:wrap;gap:10px;margin-bottom:36px}
.cert-tag{font-size:12px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;background:var(--bg);border:1px solid var(--border);border-left:3px solid var(--red);color:var(--charcoal);padding:7px 14px}
.btn-red{background:var(--red);color:#fff;font-family:'Oswald',sans-serif;font-size:14px;font-weight:600;letter-spacing:0.12em;text-transform:uppercase;padding:16px 32px;border:2px solid rgba(255,255,255,0.15);cursor:pointer;text-decoration:none;display:inline-flex;align-items:center;gap:10px;touch-action:manipulation;transition:background 0.2s,transform 0.15s,border-color 0.2s}
.btn-red:hover{background:var(--red-dark);transform:translateY(-2px);border-color:rgba(255,255,255,0.28)}

/* ── TESTIMONIALS ── */
.testimonials{background:var(--bg);padding:100px 48px 148px;position:relative}
.testimonials::after{content:'';position:absolute;bottom:0;left:0;right:0;height:72px;background:var(--charcoal);clip-path:polygon(0 0, 100% 100%, 0 100%);pointer-events:none}
.testimonials-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:24px;max-width:1200px;margin:0 auto}
.testi-card{background:#fff;border:1px solid var(--border);border-left:4px solid var(--red);padding:32px 28px}
.testi-stars{display:flex;gap:3px;margin-bottom:14px}
.testi-stars i{color:#f59e0b;font-size:14px}
.testi-text{font-size:15px;color:var(--text);line-height:1.76;font-weight:400;font-style:italic;margin-bottom:20px}
.testi-author{display:flex;align-items:center;gap:12px}
.testi-avatar{width:40px;height:40px;flex-shrink:0;background:var(--red);border-radius:50%;display:flex;align-items:center;justify-content:center;font-family:'Oswald',sans-serif;font-size:15px;font-weight:700;color:#fff}
.testi-name{font-family:'Oswald',sans-serif;font-size:14px;font-weight:600;text-transform:uppercase;letter-spacing:0.04em;color:var(--charcoal);display:block}
.testi-meta{font-size:12px;color:var(--muted);font-weight:400}

/* ── WHY US ── */
.why{background:var(--charcoal);padding:100px 48px;position:relative;overflow:hidden}
.why::before{content:'';position:absolute;inset:0;pointer-events:none;background-image:repeating-linear-gradient(-52deg,rgba(255,255,255,0.02) 0,rgba(255,255,255,0.02) 1px,transparent 1px,transparent 14px)}
.why-inner{position:relative;z-index:1;max-width:1200px;margin:0 auto;display:grid;grid-template-columns:1fr 2.2fr;gap:80px;align-items:start}
.why-left h2{font-family:'Oswald',sans-serif;font-size:clamp(34px,4.2vw,58px);font-weight:700;text-transform:uppercase;color:#fff;line-height:1.02}
.why-left h2 em{color:var(--red);font-style:normal;display:block}
.why-left p{font-size:15px;color:rgba(255,255,255,0.45);line-height:1.72;font-weight:300;margin-top:18px}
.why-grid{display:grid;grid-template-columns:1fr 1fr;gap:20px}
.why-card{border:1px solid rgba(255,255,255,0.08);padding:28px;position:relative;overflow:hidden;transition:border-color 0.3s,background 0.3s,transform 0.3s}
.why-card:hover{border-color:var(--red);background:rgba(200,16,46,0.06);transform:translateY(-3px)}
.why-card-bg-num{position:absolute;right:16px;top:10px;font-family:'Oswald',sans-serif;font-size:72px;font-weight:700;color:rgba(255,255,255,0.04);line-height:1;pointer-events:none}
.why-card i{font-size:26px;color:var(--red);margin-bottom:14px;display:block}
.why-card h3{font-family:'Oswald',sans-serif;font-size:18px;font-weight:600;text-transform:uppercase;letter-spacing:0.04em;color:#fff;margin-bottom:10px}
.why-card p{font-size:14px;color:rgba(255,255,255,0.45);line-height:1.65;font-weight:300}

/* ── CONTACT ── */
.contact{background:var(--bg);padding:100px 48px}
.contact-inner{max-width:1200px;margin:0 auto;display:grid;grid-template-columns:1fr 1.25fr;gap:80px;align-items:start}
.contact-left h2{font-family:'Oswald',sans-serif;font-size:clamp(30px,3.8vw,50px);font-weight:700;text-transform:uppercase;color:var(--charcoal);line-height:1.02;margin-bottom:14px}
.contact-left h2 em{color:var(--red);font-style:normal}
.contact-left>p{font-size:16px;color:var(--muted);line-height:1.72;font-weight:300;margin-bottom:36px;max-width:380px}
.contact-details{display:flex;flex-direction:column;gap:18px;margin-bottom:40px}
.cd-item{display:flex;align-items:flex-start;gap:14px}
.cd-icon{width:44px;height:44px;flex-shrink:0;background:var(--warm);border:1px solid var(--border);display:flex;align-items:center;justify-content:center;color:var(--red);font-size:15px}
.cd-item h4{font-family:'Oswald',sans-serif;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.1em;color:var(--muted);margin-bottom:3px}
.cd-item p,.cd-item a{font-size:15px;color:var(--text);font-weight:500;line-height:1.5;text-decoration:none}
.cd-item a:hover{color:var(--red)}
.hours-grid{display:grid;grid-template-columns:auto 1fr;gap:6px 24px;font-size:14px;margin-top:4px}
.h-day{font-weight:600;color:var(--text)}
.h-time{color:var(--muted)}
.h-open{color:#16a34a;font-weight:700}
.contact-form{background:var(--warm);border:1px solid var(--border);padding:40px}
.cf-title{font-family:'Oswald',sans-serif;font-size:22px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:var(--charcoal);margin-bottom:6px}
.cf-sub{font-size:14px;color:var(--muted);font-weight:300;margin-bottom:28px}
.cf-row{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:12px}
.cf-field{width:100%;background:var(--bg);border:1px solid var(--border);padding:13px 16px;font-family:'DM Sans',sans-serif;font-size:14px;color:var(--text);outline:none;border-radius:0;transition:border-color 0.22s}
.cf-field::placeholder{color:#b0aeaa}
.cf-field:focus{border-color:var(--red)}
select.cf-field{cursor:pointer}
textarea.cf-field{resize:vertical;min-height:110px;margin-bottom:12px}
.cf-submit{width:100%;background:var(--red);color:#fff;font-family:'Oswald',sans-serif;font-size:15px;font-weight:600;letter-spacing:0.14em;text-transform:uppercase;padding:16px;border:none;cursor:pointer;touch-action:manipulation;display:flex;align-items:center;justify-content:center;gap:10px;transition:background 0.2s,transform 0.15s}
.cf-submit:hover{background:var(--red-dark);transform:translateY(-1px)}
.cf-note{text-align:center;font-size:12px;color:var(--muted);margin-top:10px}
.cf-note i{color:#16a34a;margin-right:4px}

/* ── FOOTER ── */
footer{background:var(--charcoal);padding:60px 48px 28px;position:relative}
footer::before{content:'';position:absolute;top:0;left:0;right:0;height:3px;background:var(--red)}
.footer-grid{max-width:1200px;margin:0 auto 48px;display:grid;grid-template-columns:1.5fr 1fr 1fr 1fr;gap:48px}
.footer-logo-text{font-family:'Oswald',sans-serif;font-size:22px;font-weight:700;color:#fff;text-transform:uppercase;letter-spacing:0.06em;display:flex;align-items:center;gap:12px;margin-bottom:14px;text-decoration:none}
.footer-logo-img{height:30px;width:auto;object-fit:contain;filter:brightness(0) invert(1)}
.footer-logo-text em{color:var(--red);font-style:normal}
.footer-brand p{font-size:14px;color:rgba(255,255,255,0.38);line-height:1.72;font-weight:300;margin-bottom:20px}
.footer-social{display:flex;gap:8px}
.social-btn{width:36px;height:36px;background:rgba(255,255,255,0.07);display:flex;align-items:center;justify-content:center;color:rgba(255,255,255,0.45);font-size:14px;text-decoration:none;transition:background 0.2s,color 0.2s}
.social-btn:hover{background:var(--red);color:#fff}
.footer-col h4{font-family:'Oswald',sans-serif;font-size:13px;font-weight:600;text-transform:uppercase;letter-spacing:0.12em;color:#fff;margin-bottom:18px;padding-bottom:10px;border-bottom:1px solid rgba(255,255,255,0.08)}
.footer-links{list-style:none;display:flex;flex-direction:column;gap:9px}
.footer-links a{font-size:14px;color:rgba(255,255,255,0.38);text-decoration:none;font-weight:300;transition:color 0.2s}
.footer-links a:hover{color:var(--red)}
.footer-contact-list{list-style:none;display:flex;flex-direction:column;gap:12px}
.footer-contact-list li{display:flex;align-items:flex-start;gap:10px;font-size:13px;color:rgba(255,255,255,0.38);font-weight:300}
.footer-contact-list i{color:var(--red);flex-shrink:0;margin-top:2px}
.footer-contact-list a{color:rgba(255,255,255,0.38);text-decoration:none;transition:color 0.2s}
.footer-contact-list a:hover{color:var(--red)}
.footer-bottom{max-width:1200px;margin:0 auto;padding-top:24px;border-top:1px solid rgba(255,255,255,0.07);display:flex;align-items:center;justify-content:space-between}
.footer-bottom p{font-size:13px;color:rgba(255,255,255,0.28);font-weight:300}
.footer-tracking{display:none}

/* ── RESPONSIVE — TABLET ── */
@media (max-width: 1024px) {
  nav{padding:0 24px}
  .nav-links{gap:18px}
  .hero{height:520px}
  .hero-content{padding:60px 32px 0}
  .booking-card-wrap{padding:0 32px}
  .booking-fields{grid-template-columns:1fr 1fr;gap:10px}
  .bf-submit{grid-column:1/3;justify-content:center}
  .trust-bar{padding-top:66px}
  .services{padding:80px 32px}
  .services-grid{grid-template-columns:repeat(2,1fr)}
  .photo-banner{height:160px}
  .stats-inner{flex-wrap:wrap}
  .stat-box{flex:0 0 50%;padding:20px 16px;border-right:none;border-bottom:1px solid rgba(255,255,255,0.2)}
  .stat-box:nth-child(odd){border-right:1px solid rgba(255,255,255,0.2)}
  .stat-box:last-child{border-bottom:none}
  .about{padding:80px 32px 148px}
  .about-inner{gap:48px}
  .about-photos{height:400px}
  .testimonials{padding:80px 32px 148px}
  .testimonials-grid{grid-template-columns:repeat(2,1fr)}
  .why{padding:80px 32px}
  .why-inner{grid-template-columns:1fr;gap:36px}
  .why-grid{grid-template-columns:repeat(2,1fr)}
  .contact{padding:80px 32px}
  .contact-inner{gap:48px}
  footer{padding:48px 32px 24px}
  .footer-grid{grid-template-columns:1fr 1fr;gap:32px}
}

/* ── RESPONSIVE — MOBILE ── */
@media (max-width: 768px) {
  nav{padding:0 16px;height:58px}
  .nav-links{display:none}
  .nav-phone{display:none}
  .nav-cta{font-size:12px;padding:8px 14px}
  .nav-logo{font-size:18px}
  .hero{margin-top:58px;height:460px}
  .hero-content{padding:48px 20px 0;text-align:left}
  .hero-h1{font-size:clamp(40px,11vw,60px)}
  .hero-sub{font-size:15px;margin:0}
  .hero-trust-row{justify-content:flex-start;gap:16px;flex-wrap:wrap;margin-top:20px}
  .booking-card-wrap{padding:0 20px}
  .booking-card{padding:24px 20px}
  .booking-fields{grid-template-columns:1fr}
  .bf-submit{width:100%;justify-content:center}
  .trust-bar-inner{flex-direction:column}
  .trust-item{flex:none;width:100%;border-right:none;border-bottom:1px solid var(--border);padding:16px 20px}
  .trust-item:last-child{border-bottom:none}
  .services{padding:56px 20px}
  .section-head{margin-bottom:36px}
  .services-grid{grid-template-columns:1fr}
  .srv-card{padding:28px 20px}
  .photo-banner{display:none}
  .stats-strip{padding:36px 20px}
  .stats-inner{flex-wrap:wrap;max-width:100%}
  .stat-box{flex:0 0 50%;padding:16px 8px}
  .stat-num{font-size:40px}
  .about{padding:56px 20px 148px}
  .about-inner{grid-template-columns:1fr;gap:36px}
  .about-photos{height:240px;grid-template-columns:1fr;grid-template-rows:1fr 80px}
  .aph-main{grid-row:auto}
  .aph-sm{display:none}
  .testimonials{padding:56px 20px 148px}
  .testimonials-grid{grid-template-columns:1fr}
  .why{padding:56px 20px}
  .why-inner{grid-template-columns:1fr;gap:28px}
  .why-grid{grid-template-columns:1fr;gap:12px}
  .why-card{padding:20px}
  .contact{padding:56px 20px}
  .contact-inner{grid-template-columns:1fr;gap:40px}
  .contact-form{padding:24px 20px}
  .cf-row{grid-template-columns:1fr}
  footer{padding:40px 20px 20px}
  .footer-grid{grid-template-columns:1fr;gap:28px}
  .footer-bottom{flex-direction:column;gap:6px;text-align:center}
}
</style>
</head>
<body>

<a href="#main" class="skip-link">Skip to main content</a>

<nav aria-label="Main navigation">
  <a href="#" class="nav-logo" aria-label="PLACEHOLDER_COMPANY home">PLACEHOLDER_NAV_LOGO_T8</a>
  <ul class="nav-links">
    <li><a href="#services">Services</a></li>
    <li><a href="#about">About</a></li>
    <li><a href="#contact">Contact</a></li>
    <li><a href="#hours">Hours</a></li>
  </ul>
  <div class="nav-right">
    <a href="tel:PLACEHOLDER_PHONE_DIGITS" class="nav-phone" aria-label="Call us at PLACEHOLDER_PHONE"><i class="fa-solid fa-phone" aria-hidden="true"></i> PLACEHOLDER_PHONE</a>
    <a href="#contact" class="nav-cta">Schedule Now</a>
  </div>
</nav>

<section class="hero" id="main" aria-label="Hero">
  <div class="hero-bg" role="img" aria-label="Mechanic working on a vehicle in the shop"></div>
  <div class="hero-overlay" aria-hidden="true"></div>
  <div class="hero-content">
    <div class="hero-eyebrow">PLACEHOLDER_HERO_EYEBROW</div>
    <h1 class="hero-h1">PLACEHOLDER_HERO_H1_FORMATTED</h1>
    <p class="hero-sub">PLACEHOLDER_HERO_TAGLINE</p>
    <div class="hero-trust-row" aria-label="Key guarantees">
      <div class="hero-trust-item"><i class="fa-solid fa-shield-halved" aria-hidden="true"></i> 2-Year Warranty</div>
      <div class="hero-trust-item"><i class="fa-solid fa-bolt" aria-hidden="true"></i> Same-Day Service</div>
      <div class="hero-trust-item"><i class="fa-solid fa-star" aria-hidden="true"></i> 5-Star Rated</div>
    </div>
  </div>
  <div class="booking-card-wrap">
    <div class="booking-card">
      <div class="booking-card-label"><i class="fa-solid fa-calendar-check" aria-hidden="true"></i> Request an Appointment</div>
      <div class="booking-fields" role="form" aria-label="Quick appointment request">
        <div class="bf-group">
          <label for="bf-name">Your Name</label>
          <input class="bf-input" type="text" id="bf-name" name="name" autocomplete="name" placeholder="John Smith">
        </div>
        <div class="bf-group">
          <label for="bf-phone">Phone Number</label>
          <input class="bf-input" type="tel" id="bf-phone" name="tel" autocomplete="tel" placeholder="PLACEHOLDER_PHONE">
        </div>
        <div class="bf-group">
          <label for="bf-vehicle">Year / Make / Model</label>
          <input class="bf-input" type="text" id="bf-vehicle" name="vehicle" placeholder="2019 Honda Civic">
        </div>
        <div class="bf-group">
          <label for="bf-service">Service Needed</label>
          <select class="bf-input" id="bf-service" name="service">
            <option value="" disabled selected>Select a service</option>
            <option>Oil Change / Maintenance</option>
            <option>Brakes &amp; Suspension</option>
            <option>Engine Diagnostics</option>
            <option>AC &amp; Heating</option>
            <option>Tires &amp; Alignment</option>
            <option>Electrical / Check Engine</option>
            <option>Transmission</option>
            <option>Other</option>
          </select>
        </div>
        <button class="bf-submit" type="button"><i class="fa-solid fa-arrow-right" aria-hidden="true"></i> Book Now</button>
      </div>
    </div>
  </div>
</section>

<div class="trust-bar">
  <div class="trust-bar-inner">
    <div class="trust-item anim d1">
      <div class="trust-icon" aria-hidden="true"><i class="fa-solid fa-certificate"></i></div>
      <div><h4>ASE Certified</h4><p>Master-certified technicians on all makes &amp; models</p></div>
    </div>
    <div class="trust-item anim d2">
      <div class="trust-icon" aria-hidden="true"><i class="fa-solid fa-bolt"></i></div>
      <div><h4>Same-Day Service</h4><p>Most repairs completed the same day you drop off</p></div>
    </div>
    <div class="trust-item anim d3">
      <div class="trust-icon" aria-hidden="true"><i class="fa-solid fa-file-invoice-dollar"></i></div>
      <div><h4>No Surprise Bills</h4><p>Written estimate approved by you before any work begins</p></div>
    </div>
    <div class="trust-item anim d4">
      <div class="trust-icon" aria-hidden="true"><i class="fa-solid fa-star"></i></div>
      <div><h4>5-Star Rated</h4><p>Hundreds of verified Google reviews from local customers</p></div>
    </div>
  </div>
</div>

<section class="services" id="services">
  <div class="section-head">
    <div class="section-label anim">Our Services</div>
    <h2 class="section-h2 anim d1">Everything Your Car <em>Needs</em></h2>
    <p class="section-sub anim d2">From oil changes to engine rebuilds. If your car needs it, we handle it. One shop, every service, done right.</p>
  </div>
  <div class="services-grid">
    PLACEHOLDER_SERVICE_CARDS_T8
  </div>
</section>

<div class="photo-banner" aria-hidden="true">
  <div class="pb-cell"><img src="PLACEHOLDER_IMG/pexels-cottonbro-4489743.jpg" alt="" loading="lazy"><div class="pb-overlay"></div></div>
  <div class="pb-cell"><img src="PLACEHOLDER_IMG/c-joyful-heFTscwGDCA-unsplash.jpg" alt="" loading="lazy"><div class="pb-overlay"></div></div>
  <div class="pb-cell"><img src="PLACEHOLDER_IMG/pexels-shvetsa-4315575.jpg" alt="" loading="lazy"><div class="pb-overlay"></div></div>
</div>

<div class="stats-strip">
  <div class="stats-inner">
    <div class="stat-box anim d1"><span class="stat-num">30+</span><span class="stat-lbl">Years in Business</span></div>
    <div class="stat-box anim d2"><span class="stat-num">12K+</span><span class="stat-lbl">Vehicles Serviced</span></div>
    <div class="stat-box anim d3"><span class="stat-num">2yr</span><span class="stat-lbl">Parts &amp; Labor Warranty</span></div>
    <div class="stat-box anim d4"><span class="stat-num">5.0</span><span class="stat-lbl">Google Star Rating</span></div>
  </div>
</div>

<section class="about" id="about">
  <div class="about-inner">
    <div class="about-photos anim left">
      <div class="aph-main"><img src="PLACEHOLDER_IMG/winston-chen-VdXnpwrp1qc-unsplash.jpg" alt="Our shop floor" loading="lazy"></div>
      <div class="aph-sm"><img src="PLACEHOLDER_IMG/pexels-lumierestudiomx-4116221.jpg" alt="Engine work detail" loading="lazy"></div>
      <div class="aph-stat"><strong>$0</strong><span>Diagnostic Fee</span></div>
    </div>
    <div class="about-content">
      <div class="section-label anim">About the Shop</div>
      <h2 class="anim d1">Built on <em>Trust,</em> Running on Results</h2>
      <p class="anim d2">PLACEHOLDER_ABOUT_PARA</p>
      <ul class="about-checklist">
        <li class="anim d2"><i class="fa-solid fa-check" aria-hidden="true"></i> Serving Northeast Ohio for over 30 years</li>
        <li class="anim d3"><i class="fa-solid fa-check" aria-hidden="true"></i> ASE Master Certified technicians on staff</li>
        <li class="anim d4"><i class="fa-solid fa-check" aria-hidden="true"></i> Written estimate before any work begins</li>
        <li class="anim d5"><i class="fa-solid fa-check" aria-hidden="true"></i> 2-year / 24,000-mile parts and labor warranty</li>
        <li class="anim d6"><i class="fa-solid fa-check" aria-hidden="true"></i> Loaner vehicles available on major repairs</li>
      </ul>
      <div class="cert-row anim d2">
        <span class="cert-tag">ASE Certified</span>
        <span class="cert-tag">AAA Approved</span>
        <span class="cert-tag">NAPA AutoCare</span>
        <span class="cert-tag">BBB Accredited</span>
      </div>
      <a href="#contact" class="btn-red anim d3"><i class="fa-solid fa-calendar-check" aria-hidden="true"></i> Schedule an Appointment</a>
    </div>
  </div>
</section>

<section class="testimonials" aria-labelledby="testi-heading">
  <div class="section-head">
    <div class="section-label anim">Customer Reviews</div>
    <h2 class="section-h2 anim d1" id="testi-heading">What Our Customers <em>Say</em></h2>
    <p class="section-sub anim d2">Don't take our word for it. Here's what real Northeast Ohio drivers say about PLACEHOLDER_COMPANY.</p>
  </div>
  <div class="testimonials-grid">
    <div class="testi-card anim d1">
      <div class="testi-stars" aria-label="5 out of 5 stars"><i class="fa-solid fa-star" aria-hidden="true"></i><i class="fa-solid fa-star" aria-hidden="true"></i><i class="fa-solid fa-star" aria-hidden="true"></i><i class="fa-solid fa-star" aria-hidden="true"></i><i class="fa-solid fa-star" aria-hidden="true"></i></div>
      <p class="testi-text">"Brought my Subaru in for a check engine light other shops couldn't diagnose. Found it in an hour, fixed it same-day, and charged half what the dealer quoted. These guys are the real deal."</p>
      <div class="testi-author"><div class="testi-avatar" aria-hidden="true">MR</div><div><span class="testi-name">Mike R.</span><span class="testi-meta">Parma, OH &bull; Verified Google Review</span></div></div>
    </div>
    <div class="testi-card anim d2">
      <div class="testi-stars" aria-label="5 out of 5 stars"><i class="fa-solid fa-star" aria-hidden="true"></i><i class="fa-solid fa-star" aria-hidden="true"></i><i class="fa-solid fa-star" aria-hidden="true"></i><i class="fa-solid fa-star" aria-hidden="true"></i><i class="fa-solid fa-star" aria-hidden="true"></i></div>
      <p class="testi-text">"I've been coming here for 8 years. They never try to upsell you. When I asked about a repair, they told me honestly it could wait another 6 months. That kind of honesty keeps me coming back."</p>
      <div class="testi-author"><div class="testi-avatar" aria-hidden="true">SL</div><div><span class="testi-name">Sandra L.</span><span class="testi-meta">Brook Park, OH &bull; Verified Google Review</span></div></div>
    </div>
    <div class="testi-card anim d3">
      <div class="testi-stars" aria-label="5 out of 5 stars"><i class="fa-solid fa-star" aria-hidden="true"></i><i class="fa-solid fa-star" aria-hidden="true"></i><i class="fa-solid fa-star" aria-hidden="true"></i><i class="fa-solid fa-star" aria-hidden="true"></i><i class="fa-solid fa-star" aria-hidden="true"></i></div>
      <p class="testi-text">"Needed new brakes before a road trip. Called at 8am, dropped off at 9, picked up by noon. They texted me updates the whole time. You can tell this shop actually cares."</p>
      <div class="testi-author"><div class="testi-avatar" aria-hidden="true">DT</div><div><span class="testi-name">Derek T.</span><span class="testi-meta">Cleveland, OH &bull; Verified Google Review</span></div></div>
    </div>
  </div>
</section>

<section class="why">
  <div class="why-inner">
    <div class="why-left">
      <h2 class="anim left">Why Choose <em>Us?</em></h2>
      <p class="anim left d1">Four things we hear from every new customer who becomes a regular one.</p>
    </div>
    <div class="why-grid">
      <div class="why-card anim d1"><div class="why-card-bg-num" aria-hidden="true">01</div><i class="fa-solid fa-file-invoice-dollar" aria-hidden="true"></i><h3>Honest Pricing</h3><p>You see the full cost before we touch your car. No hidden fees, no upsells. Just the work you asked for at the price we quoted.</p></div>
      <div class="why-card anim d2"><div class="why-card-bg-num" aria-hidden="true">02</div><i class="fa-solid fa-bolt" aria-hidden="true"></i><h3>Fast Turnaround</h3><p>We know you depend on your vehicle. Most jobs are completed same-day, and we'll give you a real timeline before you drop off.</p></div>
      <div class="why-card anim d3"><div class="why-card-bg-num" aria-hidden="true">03</div><i class="fa-solid fa-wrench" aria-hidden="true"></i><h3>Expert Technicians</h3><p>Every mechanic on our floor is ASE certified and regularly trained on the latest diagnostic tools and vehicle systems.</p></div>
      <div class="why-card anim d4"><div class="why-card-bg-num" aria-hidden="true">04</div><i class="fa-solid fa-shield-halved" aria-hidden="true"></i><h3>Warranty Backed</h3><p>Every repair is backed by our 2-year / 24,000-mile parts and labor warranty. If something isn't right, we make it right.</p></div>
    </div>
  </div>
</section>

<section class="contact" id="contact">
  <div class="contact-inner">
    <div class="contact-left">
      <h2 class="anim left">Get in <em>Touch</em></h2>
      <p class="anim left d1">Schedule an appointment, ask a question, or just tell us what's going on with your car. We'll get back to you within a few hours.</p>
      <div class="contact-details">
        <div class="cd-item anim d1"><div class="cd-icon" aria-hidden="true"><i class="fa-solid fa-phone"></i></div><div><h4>Phone</h4><a href="tel:PLACEHOLDER_PHONE_DIGITS">PLACEHOLDER_PHONE</a></div></div>
        <div class="cd-item anim d2"><div class="cd-icon" aria-hidden="true"><i class="fa-solid fa-envelope"></i></div><div><h4>Email</h4><a href="mailto:PLACEHOLDER_EMAIL">PLACEHOLDER_EMAIL</a></div></div>
        <div class="cd-item anim d3"><div class="cd-icon" aria-hidden="true"><i class="fa-solid fa-location-dot"></i></div><div><h4>Address</h4><p>PLACEHOLDER_ADDRESS</p></div></div>
      </div>
      <div class="cd-item anim d4" id="hours">
        <div class="cd-icon" aria-hidden="true"><i class="fa-solid fa-clock"></i></div>
        <div><h4>Hours</h4>
          <div class="hours-grid">
            <span class="h-day">Mon &ndash; Fri</span><span class="h-time h-open">7:30 AM &ndash; 6:00 PM</span>
            <span class="h-day">Saturday</span><span class="h-time h-open">8:00 AM &ndash; 4:00 PM</span>
            <span class="h-day">Sunday</span><span class="h-time">Closed</span>
          </div>
        </div>
      </div>
    </div>
    <div class="contact-form anim right">
      <div class="cf-title">Request Service</div>
      <p class="cf-sub">Fill out the form and we'll contact you to confirm your appointment time.</p>
      <div class="cf-row">
        <label class="sr-only" for="cf-fname">First Name</label>
        <input class="cf-field" type="text" id="cf-fname" name="fname" autocomplete="given-name" placeholder="First Name">
        <label class="sr-only" for="cf-lname">Last Name</label>
        <input class="cf-field" type="text" id="cf-lname" name="lname" autocomplete="family-name" placeholder="Last Name">
      </div>
      <div class="cf-row">
        <label class="sr-only" for="cf-phone">Phone Number</label>
        <input class="cf-field" type="tel" id="cf-phone" name="phone" autocomplete="tel" placeholder="Phone Number">
        <label class="sr-only" for="cf-email">Email Address</label>
        <input class="cf-field" type="email" id="cf-email" name="email" autocomplete="email" placeholder="Email Address">
      </div>
      <div class="cf-row">
        <label class="sr-only" for="cf-vehicle">Year, Make, and Model</label>
        <input class="cf-field" type="text" id="cf-vehicle" name="vehicle" placeholder="Year / Make / Model">
        <label class="sr-only" for="cf-service">Service Needed</label>
        <select class="cf-field" id="cf-service" name="service">
          <option value="" disabled selected>Service Needed</option>
          <option>Oil Change / Maintenance</option>
          <option>Brakes &amp; Suspension</option>
          <option>Engine Diagnostics</option>
          <option>AC &amp; Heating</option>
          <option>Tires &amp; Alignment</option>
          <option>Electrical / Check Engine</option>
          <option>Transmission</option>
          <option>Other (describe below)</option>
        </select>
      </div>
      <label class="sr-only" for="cf-time">Preferred Appointment Time</label>
      <select class="cf-field" id="cf-time" name="appt-time" style="width:100%;margin-bottom:12px">
        <option value="" disabled selected>Preferred Appointment Time</option>
        <option>Morning (7:30 &ndash; 10:00 AM)</option>
        <option>Midday (10:00 AM &ndash; 1:00 PM)</option>
        <option>Afternoon (1:00 &ndash; 5:00 PM)</option>
        <option>Flexible</option>
      </select>
      <label class="sr-only" for="cf-message">Tell us more about your vehicle</label>
      <textarea class="cf-field" id="cf-message" name="message" placeholder="Tell us more about what's going on with your vehicle..."></textarea>
      <button class="cf-submit" type="button"><i class="fa-solid fa-paper-plane" aria-hidden="true"></i> Submit Request</button>
      <p class="cf-note"><i class="fa-solid fa-lock" aria-hidden="true"></i> We'll never share your information. Responses within 2 business hours.</p>
    </div>
  </div>
</section>

<footer>
  <div class="footer-grid">
    <div class="footer-brand">
      <span class="footer-logo-text">PLACEHOLDER_FOOTER_LOGO_T8</span>
      <p>Full-service auto repair for Northeast Ohio drivers. Honest work, fair prices, and a warranty you can actually count on.</p>
      <div class="footer-social">
        <a href="#" class="social-btn" aria-label="Facebook"><i class="fa-brands fa-facebook-f" aria-hidden="true"></i></a>
        <a href="#" class="social-btn" aria-label="Google"><i class="fa-brands fa-google" aria-hidden="true"></i></a>
        <a href="#" class="social-btn" aria-label="Yelp"><i class="fa-brands fa-yelp" aria-hidden="true"></i></a>
      </div>
    </div>
    <div class="footer-col">
      <h4>Services</h4>
      <ul class="footer-links">
        <li><a href="#services">Engine &amp; Drivetrain</a></li>
        <li><a href="#services">Brakes &amp; Suspension</a></li>
        <li><a href="#services">AC &amp; Heating</a></li>
        <li><a href="#services">Electrical Diagnostics</a></li>
        <li><a href="#services">Oil Changes</a></li>
        <li><a href="#services">Tires &amp; Alignment</a></li>
      </ul>
    </div>
    <div class="footer-col">
      <h4>Company</h4>
      <ul class="footer-links">
        <li><a href="#about">About Us</a></li>
        <li><a href="#contact">Schedule Service</a></li>
        <li><a href="#hours">Hours &amp; Location</a></li>
        <li><a href="#">Financing Options</a></li>
        <li><a href="#">Warranty Policy</a></li>
      </ul>
    </div>
    <div class="footer-col">
      <h4>Contact</h4>
      <ul class="footer-contact-list">
        <li><i class="fa-solid fa-phone" aria-hidden="true"></i><a href="tel:PLACEHOLDER_PHONE_DIGITS">PLACEHOLDER_PHONE</a></li>
        <li><i class="fa-solid fa-envelope" aria-hidden="true"></i><a href="mailto:PLACEHOLDER_EMAIL">PLACEHOLDER_EMAIL</a></li>
        <li><i class="fa-solid fa-location-dot" aria-hidden="true"></i>PLACEHOLDER_ADDRESS</li>
        <li><i class="fa-solid fa-clock" aria-hidden="true"></i>Mon&ndash;Fri: 7:30AM&ndash;6PM<br>Sat: 8AM&ndash;4PM</li>
      </ul>
    </div>
  </div>
  <div class="footer-bottom">
    <p>&copy; 2025 PLACEHOLDER_COMPANY. All rights reserved.</p>
    <p>Serving Cleveland, Parma, Brook Park, and all of Northeast Ohio</p>
  </div>
</footer>

<div class="footer-tracking">PLACEHOLDER_TRACKING</div>

<script>
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
if (prefersReducedMotion) {
  document.querySelectorAll('.anim').forEach(el => el.classList.add('on'));
} else {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('on'); } });
  }, { threshold: 0.12 });
  document.querySelectorAll('.anim').forEach(el => observer.observe(el));
  function animateCounter(el) {
    const target = parseFloat(el.textContent.replace(/[^0-9.]/g,''));
    const suffix = el.textContent.replace(/[0-9.]/g,'').trim();
    const isFloat = el.textContent.includes('.');
    const duration = 1800;
    const start = performance.now();
    function step(now) {
      const progress = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      const val = target * ease;
      el.textContent = (isFloat ? val.toFixed(1) : Math.round(val)) + suffix;
      if (progress < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }
  const counterObserver = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (e.isIntersecting) {
        animateCounter(e.target);
        counterObserver.unobserve(e.target);
      }
    });
  }, { threshold: 0.5 });
  document.querySelectorAll('.stat-num').forEach(el => counterObserver.observe(el));
}
</script>
</body>
</html>`;
const T9 = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>PLACEHOLDER_COMPANY | Auto Repair</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link href="https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;600;700;800;900&family=Nunito:wght@300;400;500;600;700&display=swap" rel="stylesheet">
<link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css">
<style>
PLACEHOLDER_BRAND_COLORS_T9
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
html{scroll-behavior:smooth}
body{font-family:'Nunito',sans-serif;background:var(--bg);color:var(--text);overflow-x:hidden}
img{display:block;max-width:100%}

/* ── SCROLL ANIMATIONS ── */
.anim{opacity:0;transform:translateY(38px);transition:opacity 0.8s cubic-bezier(0.22,1,0.36,1),transform 0.8s cubic-bezier(0.22,1,0.36,1)}
.anim.left{transform:translateX(-44px)}
.anim.right{transform:translateX(44px)}
.anim.on{opacity:1!important;transform:none!important}
.d1{transition-delay:.1s}.d2{transition-delay:.2s}.d3{transition-delay:.3s}
.d4{transition-delay:.4s}.d5{transition-delay:.5s}.d6{transition-delay:.6s}

/* ── TOP BAR ── */
.top-bar{
  background:var(--navy);height:44px;
  display:flex;align-items:center;justify-content:center;gap:48px;padding:0 48px;
}
.top-bar-item{
  display:flex;align-items:center;gap:8px;
  font-size:13px;font-weight:600;color:rgba(255,255,255,0.75);
}
.top-bar-item i{color:var(--gold);font-size:12px}
.top-bar-item a{color:rgba(255,255,255,0.75);text-decoration:none;transition:color 0.2s}
.top-bar-item a:hover{color:#fff}

/* ── NAV ── */
nav{
  position:sticky;top:0;z-index:300;
  background:var(--bg);border-bottom:1px solid var(--border);
  height:76px;display:flex;align-items:center;justify-content:space-between;
  padding:0 48px;box-shadow:0 2px 24px rgba(27,45,79,0.06);
}
.nav-logo{
  font-family:'Barlow Condensed',sans-serif;font-size:22px;font-weight:800;
  color:var(--navy);text-decoration:none;letter-spacing:0.04em;text-transform:uppercase;
  display:flex;align-items:center;gap:12px;
}
.nav-logo-img{height:36px;width:auto;object-fit:contain}
.nav-logo-mark{
  width:40px;height:40px;background:var(--navy);
  display:flex;align-items:center;justify-content:center;
  color:var(--gold);font-size:18px;
  font-family:'Barlow Condensed',sans-serif;font-weight:900;flex-shrink:0;
}
.nav-links{display:flex;gap:32px;list-style:none}
.nav-links a{
  font-family:'Nunito',sans-serif;font-size:14px;font-weight:700;
  color:var(--muted);text-decoration:none;
  transition:color 0.2s;position:relative;padding-bottom:4px;
}
.nav-links a::after{
  content:'';position:absolute;left:0;bottom:0;
  width:0;height:2px;background:var(--gold);transition:width 0.3s ease;
}
.nav-links a:hover{color:var(--navy)}
.nav-links a:hover::after{width:100%}
.nav-right{display:flex;align-items:center;gap:16px}
.nav-phone-link{
  font-family:'Barlow Condensed',sans-serif;font-size:20px;font-weight:700;
  color:var(--navy);text-decoration:none;letter-spacing:0.04em;
  display:flex;align-items:center;gap:9px;
}
.nav-phone-link i{
  width:32px;height:32px;background:var(--gold);
  display:flex;align-items:center;justify-content:center;
  color:#fff;font-size:13px;border-radius:50%;
}
.nav-btn{
  background:var(--navy);color:#fff;
  font-family:'Nunito',sans-serif;font-size:13px;font-weight:700;
  padding:10px 22px;border:none;cursor:pointer;text-decoration:none;
  border-radius:3px;letter-spacing:0.04em;
  transition:background 0.2s,transform 0.15s;
}
.nav-btn:hover{background:var(--navy-mid);transform:translateY(-1px)}

/* ── HERO ── */
.hero{
  display:grid;grid-template-columns:1fr 1fr;
  min-height:90vh;overflow:hidden;background:var(--cream);
}
.hero-photo-side{position:relative;overflow:hidden}
.hero-photo-bg{
  position:absolute;inset:0;
  background:linear-gradient(145deg, #1b2d4f 0%, #243a63 60%, #1b2d4f 100%);
}
.hero-photo-pattern{
  position:absolute;inset:0;
  background-image:radial-gradient(rgba(255,255,255,0.04) 1px,transparent 1px);
  background-size:24px 24px;
}
.hero-photo-tab{
  position:absolute;bottom:40px;left:0;
  background:var(--gold);color:#fff;
  font-family:'Barlow Condensed',sans-serif;
  font-size:14px;font-weight:700;letter-spacing:0.05em;text-transform:uppercase;
  padding:16px 24px;line-height:1.4;
  border-right:3px solid rgba(255,255,255,0.3);
  z-index:2;
}
.hero-photo-tab strong{display:block;font-size:26px;line-height:1;letter-spacing:0.02em}
.hero-content{
  display:flex;flex-direction:column;justify-content:center;
  padding:80px 64px;background:var(--bg);position:relative;
}
.hero-content::before{
  content:'';position:absolute;inset:0;pointer-events:none;
  background-image:radial-gradient(var(--border) 1px,transparent 1px);
  background-size:28px 28px;opacity:0.7;
}
.hero-tag{
  position:relative;z-index:1;
  display:inline-flex;align-items:center;gap:8px;
  font-size:11px;font-weight:700;letter-spacing:0.2em;text-transform:uppercase;
  color:var(--gold);margin-bottom:20px;
}
.hero-tag::before{content:'';width:24px;height:2px;background:var(--gold)}
.hero-h1{
  position:relative;z-index:1;
  font-family:'Barlow Condensed',sans-serif;
  font-size:clamp(46px,5.2vw,72px);font-weight:900;line-height:0.98;
  letter-spacing:-0.01em;
  color:var(--navy);margin-bottom:18px;
}
.hero-h1 em{font-style:normal;color:var(--gold)}
.hero-sub{
  position:relative;z-index:1;
  font-size:17px;line-height:1.75;color:var(--muted);
  font-weight:400;margin-bottom:36px;max-width:420px;
}
.hero-quick{
  position:relative;z-index:1;
  background:var(--navy);padding:28px;margin-bottom:28px;
}
.hero-quick-title{
  font-family:'Barlow Condensed',sans-serif;font-size:17px;font-weight:700;
  letter-spacing:0.04em;text-transform:uppercase;
  color:#fff;margin-bottom:16px;display:flex;align-items:center;gap:10px;
}
.hero-quick-title i{color:var(--gold)}
.hq-row{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:10px}
.hq-field{
  width:100%;background:rgba(255,255,255,0.08);
  border:1px solid rgba(255,255,255,0.15);border-radius:0;
  padding:12px 15px;font-family:'Nunito',sans-serif;font-size:14px;
  color:#fff;outline:none;transition:border-color 0.2s;
}
.hq-field::placeholder{color:rgba(255,255,255,0.38)}
.hq-field:focus{border-color:var(--gold)}
select.hq-field option{background:var(--navy);color:#fff}
.hq-submit{
  width:100%;background:var(--gold);color:#fff;
  font-family:'Nunito',sans-serif;font-size:14px;font-weight:700;
  padding:14px;border:none;cursor:pointer;
  display:flex;align-items:center;justify-content:center;gap:10px;
  margin-top:4px;letter-spacing:0.04em;
  transition:opacity 0.2s,transform 0.15s;
}
.hq-submit:hover{opacity:0.88;transform:translateY(-1px)}
.hero-badges{
  position:relative;z-index:1;
  display:flex;gap:10px;flex-wrap:wrap;
}
.hero-badge-pill{
  display:inline-flex;align-items:center;gap:7px;
  background:var(--cream);border:1px solid var(--border);
  padding:7px 14px;border-radius:100px;
  font-size:12px;font-weight:700;color:var(--navy);letter-spacing:0.04em;
}
.hero-badge-pill i{color:var(--gold);font-size:11px}

/* ── SERVICES ── */
.services{background:var(--bg);padding:100px 48px}
.section-head{text-align:center;max-width:620px;margin:0 auto 64px}
.section-eyebrow{
  font-size:11px;font-weight:700;letter-spacing:0.22em;text-transform:uppercase;
  color:var(--gold);margin-bottom:14px;display:block;
}
.section-h2{
  font-family:'Barlow Condensed',sans-serif;
  font-size:clamp(34px,4.2vw,54px);font-weight:900;
  letter-spacing:-0.01em;
  color:var(--navy);line-height:1.05;
}
.section-h2 em{font-style:normal;color:var(--gold)}
.section-sub{font-size:16px;color:var(--muted);font-weight:400;line-height:1.72;margin-top:14px}
.services-grid{
  display:grid;grid-template-columns:repeat(3,1fr);
  gap:20px;max-width:1200px;margin:0 auto;
}
.srv-card{
  background:var(--cream);border:1px solid var(--border);
  padding:36px 28px;position:relative;overflow:hidden;
  transition:transform 0.3s cubic-bezier(0.22,1,0.36,1),box-shadow 0.3s,border-color 0.3s;
}
.srv-card:hover{
  transform:translateY(-6px);
  box-shadow:0 20px 48px rgba(27,45,79,0.1);
  border-color:var(--gold);
}
.srv-card::before{
  content:'';position:absolute;top:0;left:0;right:0;
  height:3px;background:var(--gold);
  transform:scaleX(0);transform-origin:left;
  transition:transform 0.4s cubic-bezier(0.22,1,0.36,1);
}
.srv-card:hover::before{transform:scaleX(1)}
.srv-icon{
  width:54px;height:54px;background:var(--navy);
  display:flex;align-items:center;justify-content:center;
  margin-bottom:18px;border-radius:3px;transition:background 0.3s;
}
.srv-icon i{font-size:21px;color:var(--gold);transition:transform 0.3s}
.srv-card:hover .srv-icon{background:var(--gold)}
.srv-card:hover .srv-icon i{color:#fff;transform:scale(1.1)}
.srv-card h3{
  font-family:'Barlow Condensed',sans-serif;font-size:22px;font-weight:700;
  letter-spacing:0.02em;text-transform:uppercase;
  color:var(--navy);margin-bottom:12px;
}
.srv-card p{font-size:14px;color:var(--muted);line-height:1.72;font-weight:400}
.srv-more{
  display:inline-flex;align-items:center;gap:7px;
  margin-top:16px;font-size:13px;font-weight:700;
  color:var(--gold);text-decoration:none;letter-spacing:0.04em;
  transition:gap 0.25s;
}
.srv-card:hover .srv-more{gap:12px}

/* ── PHOTO WALL (CSS-only for portal) ── */
.photo-wall{
  display:grid;
  grid-template-columns:1fr 1fr 1fr;
  grid-template-rows:200px 200px;
  gap:4px;
}
.pw-cell{overflow:hidden;position:relative}
.pw-cell.tall{grid-row:span 2}
.pw-cell:nth-child(1){background:linear-gradient(145deg,#1b2d4f,#243a63)}
.pw-cell:nth-child(2){background:linear-gradient(145deg,#2a3d5e,#1b2d4f)}
.pw-cell:nth-child(3){background:linear-gradient(145deg,#243a63,#1b2d4f)}
.pw-cell:nth-child(4){background:linear-gradient(145deg,#b8883a,#9a7230)}
.pw-cell:nth-child(5){background:linear-gradient(145deg,#1b2d4f,#2a3d5e)}
.pw-pattern{
  position:absolute;inset:0;
  background-image:radial-gradient(rgba(255,255,255,0.04) 1px,transparent 1px);
  background-size:22px 22px;
}
.pw-accent{
  position:absolute;bottom:0;left:0;right:0;
  height:2px;background:var(--gold);opacity:0.4;
}

/* ── ABOUT ── */
.about{background:var(--navy);padding:100px 48px;position:relative;overflow:hidden}
.about::before{
  content:'';position:absolute;top:-100px;right:-200px;
  width:600px;height:900px;
  background:linear-gradient(135deg, rgba(184,136,58,0.06) 0%, transparent 60%);
  transform:rotate(15deg);pointer-events:none;
}
.about::after{
  content:'';position:absolute;inset:0;pointer-events:none;
  background-image:radial-gradient(rgba(255,255,255,0.03) 1px,transparent 1px);
  background-size:32px 32px;
}
.about-inner{
  position:relative;z-index:1;
  max-width:1200px;margin:0 auto;
  display:grid;grid-template-columns:1fr 1fr;gap:80px;align-items:center;
}
.about-content .section-eyebrow{text-align:left;color:var(--gold)}
.about-content h2{
  font-family:'Barlow Condensed',sans-serif;
  font-size:clamp(34px,4.2vw,58px);font-weight:900;
  letter-spacing:-0.01em;
  color:#fff;line-height:1.02;margin-bottom:20px;
}
.about-content h2 em{font-style:normal;color:var(--gold)}
.about-content>p{
  font-size:16px;color:rgba(255,255,255,0.6);
  line-height:1.8;font-weight:300;margin-bottom:28px;
}
.about-feats{
  display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:36px;
}
.about-feat{
  background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.08);
  padding:18px;transition:border-color 0.3s,background 0.3s;
}
.about-feat i{font-size:18px;color:var(--gold);margin-bottom:10px;display:block}
.about-feat h4{
  font-family:'Barlow Condensed',sans-serif;font-size:16px;font-weight:700;
  letter-spacing:0.04em;text-transform:uppercase;
  color:#fff;margin-bottom:4px;
}
.about-feat p{font-size:13px;color:rgba(255,255,255,0.45);line-height:1.5;font-weight:300}
.btn-gold{
  background:var(--gold);color:#fff;
  font-family:'Nunito',sans-serif;font-size:14px;font-weight:700;
  padding:16px 32px;border:none;cursor:pointer;text-decoration:none;
  display:inline-flex;align-items:center;gap:10px;border-radius:3px;
  transition:opacity 0.2s,transform 0.15s;
}
.btn-gold:hover{opacity:0.88;transform:translateY(-2px)}
.about-photos{display:flex;flex-direction:column;gap:10px;height:520px}
.about-photo-main{flex:1.6;overflow:hidden;position:relative}
.about-photo-main .photo-placeholder,.about-photo-sm .photo-placeholder{
  width:100%;height:100%;
}
.about-photo-main .photo-placeholder{
  background:linear-gradient(145deg,#243a63 0%,#1b2d4f 100%);
}
.about-photo-sm{flex:1;overflow:hidden}
.about-photo-sm .photo-placeholder{
  background:linear-gradient(145deg,#2a3d5e 0%,#243a63 100%);
}
.photo-placeholder-pattern{
  width:100%;height:100%;
  background-image:radial-gradient(rgba(255,255,255,0.03) 1px,transparent 1px);
  background-size:22px 22px;
}
.about-stat-strip{
  display:grid;grid-template-columns:repeat(3,1fr);gap:0;flex-shrink:0;
}
.ast-item{
  background:rgba(255,255,255,0.06);
  border:1px solid rgba(255,255,255,0.08);border-right:none;
  padding:18px;text-align:center;
}
.ast-item:last-child{border-right:1px solid rgba(255,255,255,0.08)}
.ast-item strong{
  font-family:'Barlow Condensed',sans-serif;font-size:32px;font-weight:900;
  letter-spacing:-0.01em;
  color:var(--gold);display:block;line-height:1;
}
.ast-item span{font-size:11px;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;color:rgba(255,255,255,0.4);margin-top:4px;display:block}

/* ── TESTIMONIALS ── */
.testimonials{background:var(--cream);padding:100px 48px}
.testimonials-grid{
  display:grid;grid-template-columns:repeat(3,1fr);gap:20px;
  max-width:1200px;margin:0 auto;
}
.testi-card{
  background:var(--bg);border:1px solid var(--border);padding:32px;
  position:relative;transition:transform 0.3s cubic-bezier(0.22,1,0.36,1),box-shadow 0.3s;
}
.testi-stars{color:var(--gold);font-size:14px;margin-bottom:16px;letter-spacing:2px}
.testi-quote{
  font-family:'Nunito',sans-serif;font-size:15px;font-style:italic;
  color:var(--navy);line-height:1.72;margin-bottom:20px;
}
.testi-quote::before{content:'\\201C';font-size:36px;color:var(--gold);line-height:0;vertical-align:-14px;margin-right:4px}
.testi-author{display:flex;align-items:center;gap:12px}
.testi-avatar{
  width:40px;height:40px;background:var(--navy);border-radius:50%;
  display:flex;align-items:center;justify-content:center;
  color:var(--gold);font-size:14px;flex-shrink:0;
}
.testi-name{font-size:14px;font-weight:700;color:var(--navy)}
.testi-loc{font-size:12px;color:var(--muted)}

/* ── CONTACT ── */
.contact{background:var(--bg);padding:100px 48px}
.contact-inner{
  max-width:1200px;margin:0 auto;
  display:grid;grid-template-columns:1fr 1.3fr;gap:80px;align-items:start;
}
.contact-left h2{
  font-family:'Barlow Condensed',sans-serif;
  font-size:clamp(34px,4.2vw,56px);font-weight:900;
  letter-spacing:-0.01em;
  color:var(--navy);line-height:1.02;margin-bottom:14px;
}
.contact-left h2 em{font-style:normal;color:var(--gold)}
.contact-left>p{
  font-size:16px;color:var(--muted);line-height:1.72;
  font-weight:400;margin-bottom:36px;max-width:380px;
}
.cd-blocks{display:flex;flex-direction:column;gap:16px;margin-bottom:32px}
.cd-block{
  display:flex;align-items:flex-start;gap:14px;
  padding:18px;background:var(--cream);border:1px solid var(--border);
  transition:border-color 0.2s;
}
.cd-block-icon{
  width:42px;height:42px;flex-shrink:0;background:var(--navy);
  display:flex;align-items:center;justify-content:center;
  color:var(--gold);font-size:15px;border-radius:2px;
}
.cd-block h4{
  font-size:11px;font-weight:700;letter-spacing:0.14em;text-transform:uppercase;
  color:var(--muted);margin-bottom:4px;
}
.cd-block p,.cd-block a{
  font-size:15px;font-weight:600;color:var(--navy);
  text-decoration:none;line-height:1.45;
}
.cd-block a:hover{color:var(--gold)}
.hours-list{list-style:none;display:flex;flex-direction:column;gap:6px;margin-top:4px}
.hours-list li{
  display:flex;justify-content:space-between;
  font-size:14px;color:var(--muted);padding-bottom:6px;
  border-bottom:1px solid var(--border);
}
.hours-list li:last-child{border-bottom:none}
.hours-list .open{color:#16a34a;font-weight:700}
.hours-list span:first-child{font-weight:600;color:var(--text)}
.contact-form-box{background:var(--navy);padding:44px}
.cform-title{
  font-family:'Barlow Condensed',sans-serif;font-size:28px;font-weight:900;
  letter-spacing:0.01em;
  color:#fff;margin-bottom:6px;
}
.cform-title em{font-style:normal;color:var(--gold)}
.cform-sub{font-size:14px;color:rgba(255,255,255,0.5);font-weight:300;margin-bottom:28px}
.cform-row{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:12px}
.cform-field{
  width:100%;background:rgba(255,255,255,0.07);
  border:1px solid rgba(255,255,255,0.12);border-radius:0;
  padding:13px 15px;font-family:'Nunito',sans-serif;font-size:14px;
  color:#fff;outline:none;transition:border-color 0.22s,background 0.22s;
}
.cform-field::placeholder{color:rgba(255,255,255,0.35)}
.cform-field:focus{border-color:var(--gold);background:rgba(255,255,255,0.1)}
select.cform-field option{background:var(--navy);color:#fff}
textarea.cform-field{resize:vertical;min-height:110px;margin-bottom:12px}
.cform-submit{
  width:100%;background:var(--gold);color:#fff;
  font-family:'Nunito',sans-serif;font-size:15px;font-weight:700;
  padding:16px;border:none;cursor:pointer;
  display:flex;align-items:center;justify-content:center;gap:10px;
  border-radius:3px;letter-spacing:0.04em;
  transition:opacity 0.2s,transform 0.15s;
}
.cform-submit:hover{opacity:0.88;transform:translateY(-1px)}
.cform-note{text-align:center;font-size:12px;color:rgba(255,255,255,0.35);margin-top:10px}
.cform-note i{color:#4ade80;margin-right:4px}

/* ── MAP STRIP ── */
.map-strip{
  background:var(--navy);padding:20px 48px;
  display:flex;align-items:center;justify-content:center;gap:48px;flex-wrap:wrap;
}
.map-strip-item{
  display:flex;align-items:center;gap:10px;
  font-size:14px;font-weight:600;color:rgba(255,255,255,0.7);
}
.map-strip-item i{color:var(--gold)}
.map-strip-item a{color:rgba(255,255,255,0.7);text-decoration:none}
.map-strip-item a:hover{color:#fff}

/* ── FOOTER ── */
footer{background:var(--navy);padding:64px 48px 28px;position:relative}
footer::before{content:'';position:absolute;top:0;left:0;right:0;height:4px;background:var(--gold)}
.footer-grid{
  max-width:1200px;margin:0 auto 48px;
  display:grid;grid-template-columns:1.4fr 1fr 1fr 1fr;gap:48px;
}
.footer-logo{
  font-family:'Barlow Condensed',sans-serif;font-size:22px;font-weight:900;
  letter-spacing:0.04em;text-transform:uppercase;
  color:#fff;display:flex;align-items:center;gap:12px;margin-bottom:14px;text-decoration:none;
}
.footer-logo-img{height:32px;width:auto;object-fit:contain;filter:brightness(0) invert(1)}
.footer-logo-mark{
  width:38px;height:38px;background:var(--gold);
  display:flex;align-items:center;justify-content:center;
  color:#fff;font-size:14px;border-radius:2px;
}
.footer-brand p{font-size:14px;color:rgba(255,255,255,0.38);line-height:1.72;font-weight:300;margin-bottom:20px}
.footer-social{display:flex;gap:8px}
.social-btn{
  width:36px;height:36px;background:rgba(255,255,255,0.07);
  display:flex;align-items:center;justify-content:center;
  color:rgba(255,255,255,0.45);font-size:14px;text-decoration:none;
  border-radius:2px;transition:background 0.2s,color 0.2s;
}
.social-btn:hover{background:var(--gold);color:#fff}
.footer-col h4{
  font-family:'Barlow Condensed',sans-serif;font-size:15px;font-weight:800;
  letter-spacing:0.1em;text-transform:uppercase;
  color:#fff;margin-bottom:18px;padding-bottom:10px;
  border-bottom:1px solid rgba(255,255,255,0.08);
}
.footer-links{list-style:none;display:flex;flex-direction:column;gap:9px}
.footer-links a{font-size:14px;color:rgba(255,255,255,0.38);text-decoration:none;font-weight:300;transition:color 0.2s;display:flex;align-items:center;gap:7px}
.footer-links a i{font-size:11px;color:var(--gold)}
.footer-links a:hover{color:#fff}
.footer-info-list{list-style:none;display:flex;flex-direction:column;gap:12px}
.footer-info-list li{display:flex;align-items:flex-start;gap:10px;font-size:13px;color:rgba(255,255,255,0.38);font-weight:300}
.footer-info-list i{color:var(--gold);flex-shrink:0;margin-top:2px}
.footer-info-list a{color:rgba(255,255,255,0.38);text-decoration:none;transition:color 0.2s}
.footer-info-list a:hover{color:var(--gold)}
.footer-bottom{
  max-width:1200px;margin:0 auto;
  padding-top:24px;border-top:1px solid rgba(255,255,255,0.07);
  display:flex;align-items:center;justify-content:space-between;
}
.footer-bottom p{font-size:13px;color:rgba(255,255,255,0.28);font-weight:300}
.footer-tracking{display:none}

/* ── RESPONSIVE — TABLET ── */
@media (max-width: 1024px) {
  .top-bar{gap:24px;padding:0 24px;font-size:12px}
  .top-bar-item{gap:6px}
  nav{padding:0 24px;height:68px}
  .nav-links{gap:20px}
  .nav-phone-link{font-size:16px}
  .hero{min-height:auto}
  .hero-content{padding:56px 40px}
  .services{padding:80px 32px}
  .services-grid{grid-template-columns:repeat(2,1fr);gap:14px}
  .photo-wall{grid-template-rows:220px 220px}
  .about{padding:80px 32px}
  .about-inner{gap:48px}
  .about-photos{gap:8px}
  .testimonials{padding:80px 32px}
  .testimonials-grid{grid-template-columns:1fr;max-width:560px}
  .contact{padding:80px 32px}
  .contact-inner{gap:48px}
  footer{padding:48px 32px 24px}
  .footer-grid{grid-template-columns:1fr 1fr;gap:32px}
}

/* ── RESPONSIVE — MOBILE ── */
@media (max-width: 768px) {
  .top-bar{display:none}

  nav{padding:0 16px;height:64px}
  .nav-links{display:none}
  .nav-right{gap:10px}
  .nav-phone-link{font-size:14px}
  .nav-phone-link i{width:26px;height:26px;font-size:11px}
  .nav-btn{font-size:12px;padding:8px 12px}
  .nav-logo{font-size:17px}
  .nav-logo-mark{width:34px;height:34px;font-size:14px}

  .hero{grid-template-columns:1fr}
  .hero-photo-side{height:220px;position:relative}
  .hero-photo-bg,.hero-photo-pattern{position:absolute;inset:0}
  .hero-photo-tab{bottom:16px;font-size:12px;padding:12px 18px}
  .hero-photo-tab strong{font-size:20px}
  .hero-content{padding:36px 20px}
  .hero-h1{font-size:clamp(30px,8vw,46px);margin-bottom:14px}
  .hero-sub{font-size:15px;margin-bottom:24px;max-width:100%}
  .hero-quick{padding:20px;margin-bottom:20px}
  .hero-quick-title{font-size:15px;margin-bottom:12px}
  .hq-row{grid-template-columns:1fr}
  .hero-badges{gap:8px}
  .hero-badge-pill{font-size:11px;padding:6px 11px}

  .services{padding:56px 20px}
  .section-head{margin-bottom:36px}
  .services-grid{grid-template-columns:1fr;gap:12px}
  .srv-card{padding:28px 20px}

  .photo-wall{
    grid-template-columns:1fr 1fr;
    grid-template-rows:repeat(3,130px);
  }
  .pw-cell.tall{grid-row:auto}

  .about{padding:56px 20px}
  .about-inner{grid-template-columns:1fr;gap:36px}
  .about-feats{grid-template-columns:1fr}
  .about-photos{height:auto;flex-direction:column;gap:6px}
  .about-photo-main{height:200px;flex:none}
  .about-photo-sm{display:none}
  .about-stat-strip{grid-template-columns:repeat(3,1fr);flex-shrink:0}
  .ast-item strong{font-size:22px}

  .testimonials{padding:56px 20px}
  .testimonials-grid{grid-template-columns:1fr}

  .contact{padding:56px 20px}
  .contact-inner{grid-template-columns:1fr;gap:36px}
  .contact-form-box{padding:28px 20px}
  .cform-title{font-size:22px}
  .cform-row{grid-template-columns:1fr}

  .map-strip{padding:16px 20px;gap:14px;flex-direction:column;align-items:flex-start}

  footer{padding:40px 20px 20px}
  .footer-grid{grid-template-columns:1fr;gap:28px}
  .footer-bottom{flex-direction:column;gap:6px;text-align:center}
  .footer-bottom p{font-size:12px}
}
</style>
</head>
<body>

<div class="top-bar">
  <div class="top-bar-item"><i class="fa-solid fa-phone"></i> <a href="tel:PLACEHOLDER_PHONE_DIGITS">PLACEHOLDER_PHONE</a></div>
  <div class="top-bar-item"><i class="fa-solid fa-clock"></i> Mon&ndash;Fri 7:30AM&ndash;6PM &nbsp;|&nbsp; Sat 8AM&ndash;4PM</div>
  <div class="top-bar-item"><i class="fa-solid fa-location-dot"></i> PLACEHOLDER_ADDRESS</div>
</div>

<nav>
  <a href="#" class="nav-logo">PLACEHOLDER_NAV_LOGO_T9</a>
  <ul class="nav-links">
    <li><a href="#services">Services</a></li>
    <li><a href="#about">About</a></li>
    <li><a href="#testimonials">Reviews</a></li>
    <li><a href="#contact">Contact</a></li>
  </ul>
  <div class="nav-right">
    <a href="tel:PLACEHOLDER_PHONE_DIGITS" class="nav-phone-link">
      <i class="fa-solid fa-phone"></i> PLACEHOLDER_PHONE
    </a>
    <a href="#contact" class="nav-btn">Book Appointment</a>
  </div>
</nav>

<section class="hero">
  <div class="hero-photo-side">
    <img src="PLACEHOLDER_IMG/pexels-gustavo-fring-6870298.jpg" alt="" style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;">
    <div class="hero-photo-tab">
      <strong>30+</strong>
      Years Serving the Community
    </div>
  </div>
  <div class="hero-content">
    <div class="hero-tag">PLACEHOLDER_HERO_EYEBROW</div>
    <h1 class="hero-h1">PLACEHOLDER_HERO_H1</h1>
    <p class="hero-sub">PLACEHOLDER_HERO_TAGLINE</p>
    <div class="hero-quick">
      <div class="hero-quick-title"><i class="fa-solid fa-calendar-check"></i> Quick Appointment Request</div>
      <div class="hq-row">
        <input class="hq-field" type="text" placeholder="Your Name">
        <input class="hq-field" type="tel" placeholder="Phone Number">
      </div>
      <select class="hq-field" style="width:100%;margin-bottom:10px">
        <option value="" disabled selected>What Do You Need?</option>
        <option>Oil Change</option>
        <option>Brakes</option>
        <option>Check Engine Light</option>
        <option>AC or Heat</option>
        <option>Tires / Alignment</option>
        <option>General Repair</option>
        <option>Other</option>
      </select>
      <button class="hq-submit" type="button"><i class="fa-solid fa-paper-plane"></i> Send Request &mdash; We'll Call You</button>
    </div>
    <div class="hero-badges">
      <span class="hero-badge-pill"><i class="fa-solid fa-certificate"></i> ASE Certified</span>
      <span class="hero-badge-pill"><i class="fa-solid fa-shield-halved"></i> 2yr/24K Warranty</span>
      <span class="hero-badge-pill"><i class="fa-solid fa-star"></i> 5-Star Google Rating</span>
      <span class="hero-badge-pill"><i class="fa-solid fa-hand-holding-dollar"></i> Free Diagnostics</span>
    </div>
  </div>
</section>

<section class="services" id="services">
  <div class="section-head">
    <span class="section-eyebrow anim">What We Do</span>
    <h2 class="section-h2 anim d1">Full-Service Repair, <em>Done Right</em></h2>
    <p class="section-sub anim d2">Every service your vehicle needs — performed by certified technicians who care about getting it right the first time.</p>
  </div>
  <div class="services-grid">
    PLACEHOLDER_SERVICE_CARDS_T9
  </div>
</section>

<div class="photo-wall">
  <div class="pw-cell tall"><img src="PLACEHOLDER_IMG/pexels-gustavo-fring-6870319.jpg" alt="" style="width:100%;height:100%;object-fit:cover;display:block;"></div>
  <div class="pw-cell"><img src="PLACEHOLDER_IMG/pexels-shvetsa-4315574.jpg" alt="" style="width:100%;height:100%;object-fit:cover;display:block;"></div>
  <div class="pw-cell"><img src="PLACEHOLDER_IMG/mehmet-talha-onuk-rU_bvNfr8pQ-unsplash.jpg" alt="" style="width:100%;height:100%;object-fit:cover;display:block;"></div>
  <div class="pw-cell"><img src="PLACEHOLDER_IMG/dextar-vision-gW34cv-Ojjs-unsplash.jpg" alt="" style="width:100%;height:100%;object-fit:cover;display:block;"></div>
  <div class="pw-cell"><img src="PLACEHOLDER_IMG/pexels-shvetsa-4315575.jpg" alt="" style="width:100%;height:100%;object-fit:cover;display:block;"></div>
</div>

<section class="about" id="about">
  <div class="about-inner">
    <div class="about-content">
      <span class="section-eyebrow anim">About the Shop</span>
      <h2 class="anim d1">Your Neighborhood Mechanic,<br><em>30 Years Running</em></h2>
      <p class="anim d2">PLACEHOLDER_ABOUT_PARA</p>
      <div class="about-feats">
        <div class="about-feat anim d2">
          <i class="fa-solid fa-certificate"></i>
          <h4>ASE Certified</h4>
          <p>Every technician carries current ASE certification and ongoing training on modern vehicles.</p>
        </div>
        <div class="about-feat anim d3">
          <i class="fa-solid fa-file-invoice-dollar"></i>
          <h4>Honest Estimates</h4>
          <p>Written quote before any work begins. You approve everything &mdash; no exceptions, ever.</p>
        </div>
        <div class="about-feat anim d4">
          <i class="fa-solid fa-bolt"></i>
          <h4>Same-Day Service</h4>
          <p>Most repairs done same day. We'll give you a realistic timeline when you drop off.</p>
        </div>
        <div class="about-feat anim d5">
          <i class="fa-solid fa-shield-halved"></i>
          <h4>2yr/24K Warranty</h4>
          <p>Every repair covered by our parts and labor warranty. If it's not right, we fix it free.</p>
        </div>
      </div>
      <a href="#contact" class="btn-gold anim d3"><i class="fa-solid fa-calendar-check"></i> Book an Appointment</a>
    </div>
    <div class="about-photos anim right">
      <div class="about-photo-main">
        <img src="PLACEHOLDER_IMG/winston-chen-VdXnpwrp1qc-unsplash.jpg" alt="" style="width:100%;height:100%;object-fit:cover;display:block;">
      </div>
      <div class="about-photo-sm">
        <img src="PLACEHOLDER_IMG/pexels-lumierestudiomx-4116221.jpg" alt="" style="width:100%;height:100%;object-fit:cover;display:block;">
      </div>
      <div class="about-stat-strip">
        <div class="ast-item"><strong>30+</strong><span>Years Open</span></div>
        <div class="ast-item"><strong>5.0&#9733;</strong><span>Google Rating</span></div>
        <div class="ast-item"><strong>$0</strong><span>Diag. Fee</span></div>
      </div>
    </div>
  </div>
</section>

<section class="testimonials" id="testimonials">
  <div class="section-head">
    <span class="section-eyebrow anim">Customer Reviews</span>
    <h2 class="section-h2 anim d1">What the Neighborhood <em>Says</em></h2>
  </div>
  <div class="testimonials-grid">
    <div class="testi-card anim d1">
      <div class="testi-stars"><i class="fa-solid fa-star"></i><i class="fa-solid fa-star"></i><i class="fa-solid fa-star"></i><i class="fa-solid fa-star"></i><i class="fa-solid fa-star"></i></div>
      <p class="testi-quote">Took my car in for a check engine light that two other shops couldn't figure out. These guys found the problem in an hour, fixed it same day, and charged me way less than I expected. I'm never going anywhere else.</p>
      <div class="testi-author">
        <div class="testi-avatar"><i class="fa-solid fa-user"></i></div>
        <div><div class="testi-name">Mike D.</div><div class="testi-loc">Google Review &mdash; 5 Stars</div></div>
      </div>
    </div>
    <div class="testi-card anim d2">
      <div class="testi-stars"><i class="fa-solid fa-star"></i><i class="fa-solid fa-star"></i><i class="fa-solid fa-star"></i><i class="fa-solid fa-star"></i><i class="fa-solid fa-star"></i></div>
      <p class="testi-quote">Honest, fast, and fair. They showed me exactly what was wrong before they did anything. I've been bringing both our family cars here for 8 years and I've always felt like they treated me right.</p>
      <div class="testi-author">
        <div class="testi-avatar"><i class="fa-solid fa-user"></i></div>
        <div><div class="testi-name">Sandra K.</div><div class="testi-loc">Google Review &mdash; 5 Stars</div></div>
      </div>
    </div>
    <div class="testi-card anim d3">
      <div class="testi-stars"><i class="fa-solid fa-star"></i><i class="fa-solid fa-star"></i><i class="fa-solid fa-star"></i><i class="fa-solid fa-star"></i><i class="fa-solid fa-star"></i></div>
      <p class="testi-quote">My brakes were metal on metal and I was panicking. Called them at 8AM, car was done by noon. They even called to check in a week later. You don't get that kind of service anymore.</p>
      <div class="testi-author">
        <div class="testi-avatar"><i class="fa-solid fa-user"></i></div>
        <div><div class="testi-name">James R.</div><div class="testi-loc">Google Review &mdash; 5 Stars</div></div>
      </div>
    </div>
  </div>
</section>

<section class="contact" id="contact">
  <div class="contact-inner">
    <div class="contact-left">
      <h2 class="anim left">Schedule <em>Service</em></h2>
      <p class="anim left d1">Call, drop in, or fill out the form. We'll get back to you within a couple hours and get your car in as soon as possible &mdash; usually same or next day.</p>
      <div class="cd-blocks">
        <div class="cd-block anim d1">
          <div class="cd-block-icon"><i class="fa-solid fa-phone"></i></div>
          <div><h4>Phone</h4><a href="tel:PLACEHOLDER_PHONE_DIGITS">PLACEHOLDER_PHONE</a></div>
        </div>
        <div class="cd-block anim d2">
          <div class="cd-block-icon"><i class="fa-solid fa-envelope"></i></div>
          <div><h4>Email</h4><a href="mailto:PLACEHOLDER_EMAIL">PLACEHOLDER_EMAIL</a></div>
        </div>
        <div class="cd-block anim d3">
          <div class="cd-block-icon"><i class="fa-solid fa-location-dot"></i></div>
          <div><h4>Address</h4><p>PLACEHOLDER_ADDRESS</p></div>
        </div>
      </div>
      <div class="anim d4">
        <h4 style="font-size:11px;font-weight:700;letter-spacing:0.14em;text-transform:uppercase;color:var(--muted);margin-bottom:10px">Shop Hours</h4>
        <ul class="hours-list">
          <li><span>Monday &ndash; Friday</span><span class="open">7:30 AM &ndash; 6:00 PM</span></li>
          <li><span>Saturday</span><span class="open">8:00 AM &ndash; 4:00 PM</span></li>
          <li><span>Sunday</span><span>Closed</span></li>
        </ul>
      </div>
    </div>
    <div class="contact-form-box anim right">
      <div class="cform-title">Book an <em>Appointment</em></div>
      <p class="cform-sub">Fill out the form and we'll call or email to confirm your appointment within 2 hours.</p>
      <div class="cform-row">
        <input class="cform-field" type="text" placeholder="First Name">
        <input class="cform-field" type="text" placeholder="Last Name">
      </div>
      <div class="cform-row">
        <input class="cform-field" type="tel" placeholder="Phone Number">
        <input class="cform-field" type="email" placeholder="Email Address">
      </div>
      <div class="cform-row">
        <input class="cform-field" type="text" placeholder="Year / Make / Model">
        <input class="cform-field" type="text" placeholder="Approximate Mileage">
      </div>
      <select class="cform-field" style="width:100%;margin-bottom:12px">
        <option value="" disabled selected>Service Needed</option>
        <option>Oil Change / Routine Maintenance</option>
        <option>Brakes &amp; Rotors</option>
        <option>Check Engine Light</option>
        <option>AC or Heating</option>
        <option>Tires &amp; Alignment</option>
        <option>Engine or Transmission</option>
        <option>Electrical Issue</option>
        <option>Other &mdash; I'll describe below</option>
      </select>
      <select class="cform-field" style="width:100%;margin-bottom:12px">
        <option value="" disabled selected>Preferred Appointment Day</option>
        <option>Monday</option>
        <option>Tuesday</option>
        <option>Wednesday</option>
        <option>Thursday</option>
        <option>Friday</option>
        <option>Saturday Morning</option>
        <option>Flexible &mdash; Any Day Works</option>
      </select>
      <textarea class="cform-field" placeholder="Describe what's going on with your vehicle (optional but helpful)..."></textarea>
      <button class="cform-submit" type="button"><i class="fa-solid fa-calendar-check"></i> Submit Appointment Request</button>
      <p class="cform-note"><i class="fa-solid fa-lock"></i> Your info is never shared. We respond within 2 business hours.</p>
    </div>
  </div>
</section>

<div class="map-strip">
  <div class="map-strip-item"><i class="fa-solid fa-location-dot"></i> PLACEHOLDER_ADDRESS</div>
  <div class="map-strip-item"><i class="fa-solid fa-phone"></i> <a href="tel:PLACEHOLDER_PHONE_DIGITS">PLACEHOLDER_PHONE</a></div>
  <div class="map-strip-item"><i class="fa-solid fa-clock"></i> Mon&ndash;Fri 7:30&ndash;6 | Sat 8&ndash;4</div>
  <div class="map-strip-item"><i class="fa-brands fa-google"></i> 5.0 Stars on Google</div>
</div>

<footer>
  <div class="footer-grid">
    <div class="footer-brand">
      <a href="#" class="footer-logo">PLACEHOLDER_FOOTER_LOGO_T9</a>
      <p>Family-owned auto repair. Honest work, certified technicians, and a warranty you can actually count on.</p>
      <div class="footer-social">
        <a href="#" class="social-btn"><i class="fa-brands fa-facebook-f"></i></a>
        <a href="#" class="social-btn"><i class="fa-brands fa-google"></i></a>
        <a href="#" class="social-btn"><i class="fa-brands fa-yelp"></i></a>
      </div>
    </div>
    <div class="footer-col">
      <h4>Services</h4>
      <ul class="footer-links">
        <li><a href="#services"><i class="fa-solid fa-chevron-right"></i> Oil Changes</a></li>
        <li><a href="#services"><i class="fa-solid fa-chevron-right"></i> Brakes &amp; Rotors</a></li>
        <li><a href="#services"><i class="fa-solid fa-chevron-right"></i> Check Engine Light</a></li>
        <li><a href="#services"><i class="fa-solid fa-chevron-right"></i> AC &amp; Heating</a></li>
        <li><a href="#services"><i class="fa-solid fa-chevron-right"></i> Tires &amp; Alignment</a></li>
        <li><a href="#services"><i class="fa-solid fa-chevron-right"></i> Engine &amp; Transmission</a></li>
      </ul>
    </div>
    <div class="footer-col">
      <h4>Company</h4>
      <ul class="footer-links">
        <li><a href="#about"><i class="fa-solid fa-chevron-right"></i> About Us</a></li>
        <li><a href="#testimonials"><i class="fa-solid fa-chevron-right"></i> Customer Reviews</a></li>
        <li><a href="#contact"><i class="fa-solid fa-chevron-right"></i> Book Appointment</a></li>
        <li><a href="#"><i class="fa-solid fa-chevron-right"></i> Warranty Policy</a></li>
        <li><a href="#"><i class="fa-solid fa-chevron-right"></i> Financing Available</a></li>
      </ul>
    </div>
    <div class="footer-col">
      <h4>Contact</h4>
      <ul class="footer-info-list">
        <li><i class="fa-solid fa-phone"></i><a href="tel:PLACEHOLDER_PHONE_DIGITS">PLACEHOLDER_PHONE</a></li>
        <li><i class="fa-solid fa-envelope"></i><a href="mailto:PLACEHOLDER_EMAIL">PLACEHOLDER_EMAIL</a></li>
        <li><i class="fa-solid fa-location-dot"></i>PLACEHOLDER_ADDRESS</li>
        <li><i class="fa-solid fa-clock"></i>Mon&ndash;Fri 7:30AM&ndash;6PM<br>Sat 8:00AM&ndash;4PM</li>
      </ul>
    </div>
  </div>
  <div class="footer-bottom">
    <p>&copy; 2025 PLACEHOLDER_COMPANY. All rights reserved.</p>
    <p>Proudly serving the local community</p>
  </div>
</footer>

<div class="footer-tracking">PLACEHOLDER_TRACKING</div>

<script>
const observer = new IntersectionObserver((entries) => {
  entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('on'); });
}, { threshold: 0.1 });
document.querySelectorAll('.anim').forEach(el => observer.observe(el));

function countUp(el) {
  const raw = el.textContent;
  const num = parseFloat(raw.replace(/[^\\d.]/g, ''));
  const suffix = raw.replace(/[\\d.]/g, '').trim();
  const dec = raw.includes('.') ? 1 : 0;
  const dur = 1600;
  const t0 = performance.now();
  const tick = (t) => {
    const p = Math.min((t - t0) / dur, 1);
    const ease = 1 - Math.pow(1 - p, 3);
    el.textContent = (num * ease).toFixed(dec) + suffix;
    if (p < 1) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}
const cObserver = new IntersectionObserver((entries) => {
  entries.forEach(e => {
    if (e.isIntersecting) { countUp(e.target); cObserver.unobserve(e.target); }
  });
}, { threshold: 0.6 });
document.querySelectorAll('.ast-item strong').forEach(el => cObserver.observe(el));
</script>
</body>
</html>`;

export function generateAutomotiveMockup(templateId, data) {
  const {
    company = 'Your Company',
    phone = '',
    email = '',
    address = '',
    logoUrl = '',
    services = null,
    copy = null,
  } = data;

  const phoneDigits = phone.replace(/\D/g, '');

  function navLogoT8() {
    if (logoUrl) return `<img src="${logoUrl}" alt="${company}" class="nav-logo-img"> ${company}`;
    return company;
  }
  function footerLogoT8() {
    if (logoUrl) return `<img src="${logoUrl}" alt="${company}" class="footer-logo-img"> ${company}`;
    return company;
  }
  function navLogoT9() {
    if (logoUrl) return `<img src="${logoUrl}" alt="${company}" class="nav-logo-img">`;
    return `<div class="nav-logo-mark"><i class="fa-solid fa-wrench"></i></div> ${company}`;
  }
  function footerLogoT9() {
    if (logoUrl) return `<img src="${logoUrl}" alt="${company}" class="footer-logo-img"> ${company}`;
    return `<div class="footer-logo-mark"><i class="fa-solid fa-wrench"></i></div> ${company}`;
  }

  if (templateId === 8) {
    const tmplCopy = copy || AUTOMOTIVE_DEFAULT_COPY.T8;
    const defaultSvcs8 = AUTOMOTIVE_DEFAULT_SERVICES.T8;
    const rawSvcs8 = services || (copy && Array.isArray(copy.services) && copy.services.length >= 3 ? copy.services.slice(0, 6) : null) || defaultSvcs8;
    const tmplServices = rawSvcs8.map((s, i) => ({
      title: s.title || s.name || 'Auto Service',
      icon: s.icon || defaultSvcs8[i % defaultSvcs8.length].icon,
      desc: s.desc || '',
    }));
    const serviceCards = buildAutoServiceCardsT8(tmplServices);

    const colors = `
      :root {
        --bg: #f8f7f5;
        --warm: #f2efe9;
        --light: #ebe8e2;
        --border: #e0dcd6;
        --charcoal: #1a1a1c;
        --charcoal-mid: #282830;
        --red: #c8102e;
        --red-dark: #a60d26;
        --text: #1a1a1c;
        --muted: #635f5a;
      }
    `;

    return T8
      .replace('PLACEHOLDER_BRAND_COLORS_T8', colors)
      .replace(/PLACEHOLDER_COMPANY/g, company)
      .replace(/PLACEHOLDER_PHONE_DIGITS/g, phoneDigits)
      .replace(/PLACEHOLDER_PHONE/g, phone)
      .replace(/PLACEHOLDER_EMAIL/g, email)
      .replace(/PLACEHOLDER_ADDRESS/g, address)
      .replace('PLACEHOLDER_NAV_LOGO_T8', navLogoT8())
      .replace('PLACEHOLDER_FOOTER_LOGO_T8', footerLogoT8())
      .replace('PLACEHOLDER_HERO_EYEBROW', tmplCopy.eyebrow)
      .replace('PLACEHOLDER_HERO_H1_FORMATTED', (() => {
        const words = tmplCopy.headline.split(' ');
        if (words.length <= 1) return tmplCopy.headline;
        return words.slice(0, -1).join(' ') + ' <em>' + words[words.length - 1] + '</em>';
      })())
      .replace('PLACEHOLDER_HERO_TAGLINE', tmplCopy.tagline)
      .replace('PLACEHOLDER_ABOUT_PARA', tmplCopy.about)
      .replace('PLACEHOLDER_SERVICE_CARDS_T8', serviceCards)
      .replace('PLACEHOLDER_TRACKING', '')
      .replace(/PLACEHOLDER_IMG/g, 'https://semdeckdigital.github.io/semdeck-claude-mockups/images');
  }

  if (templateId === 9) {
    const tmplCopy = copy || AUTOMOTIVE_DEFAULT_COPY.T9;
    const defaultSvcs9 = AUTOMOTIVE_DEFAULT_SERVICES.T9;
    const rawSvcs9 = services || (copy && Array.isArray(copy.services) && copy.services.length >= 3 ? copy.services.slice(0, 6) : null) || defaultSvcs9;
    const tmplServices = rawSvcs9.map((s, i) => ({
      title: s.title || s.name || 'Auto Service',
      icon: s.icon || defaultSvcs9[i % defaultSvcs9.length].icon,
      desc: s.desc || '',
    }));
    const serviceCards = buildAutoServiceCardsT9(tmplServices);

    const colors = `
      :root {
        --bg: #faf8f4;
        --cream: #f4f0e8;
        --cream-dark: #ede8dc;
        --navy: #1b2d4f;
        --navy-mid: #243a63;
        --navy-light: rgba(27,45,79,0.07);
        --gold: #b8883a;
        --gold-light: rgba(184,136,58,0.12);
        --gold-border: rgba(184,136,58,0.3);
        --text: #1b2d4f;
        --muted: #6b7280;
        --border: #e4dfd5;
      }
    `;

    return T9
      .replace('PLACEHOLDER_BRAND_COLORS_T9', colors)
      .replace(/PLACEHOLDER_COMPANY/g, company)
      .replace(/PLACEHOLDER_PHONE_DIGITS/g, phoneDigits)
      .replace(/PLACEHOLDER_PHONE/g, phone)
      .replace(/PLACEHOLDER_EMAIL/g, email)
      .replace(/PLACEHOLDER_ADDRESS/g, address)
      .replace('PLACEHOLDER_NAV_LOGO_T9', navLogoT9())
      .replace('PLACEHOLDER_FOOTER_LOGO_T9', footerLogoT9())
      .replace('PLACEHOLDER_HERO_EYEBROW', tmplCopy.eyebrow)
      .replace('PLACEHOLDER_HERO_H1', tmplCopy.headline)
      .replace('PLACEHOLDER_HERO_TAGLINE', tmplCopy.tagline)
      .replace('PLACEHOLDER_ABOUT_PARA', tmplCopy.about)
      .replace('PLACEHOLDER_SERVICE_CARDS_T9', serviceCards)
      .replace('PLACEHOLDER_TRACKING', '')
      .replace(/PLACEHOLDER_IMG/g, 'https://semdeckdigital.github.io/semdeck-claude-mockups/images');
  }

  return '<p>Unknown automotive template ID</p>';
}
